// src/views/InvoiceDetailsView.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import LineDescription from '../components/documents/LineDescription'; // linetext-4d8b2e61 — line descriptions keep line breaks, indents and bullets
import { LINE_NAME, LINE_DESC, LINE_NUM } from '../components/documents/lineItemStyles'; // linetext-4d8b2e61 — the PDF's line layout
import { useNavigate } from 'react-router-dom'; // invqlink-b7d2e9c4 -- deep-link the Estimate # to its quote
import { db, functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, deleteField, collection, query, where, onSnapshot, getDocs } from 'firebase/firestore'; // paysyncimp-2c6a9f38 impayrows-3d84c6f1 (collection/query/where/onSnapshot are read-only) jobnum-3e8c1f92 (getDoc resolves a legacy Job #); getDocs: custcopy-32471cbd 8d
import { isChecklistOfficeRole, invoiceChecklistChoices, CHECKLIST_UNFINISHED_ASK, canSendToAnyAddress } from '../utils/checklistClient'; // custcopy-32471cbd 8d — attach checklists to the invoice email; canSendToAnyAddress: clfix-56ea256b
import { can, PERMISSIONS, canRecordPayment, canPushToQuickBooks } from '../utils/permissions'; // paysyncimp-2c6a9f38; canRecordPayment: staffpay-6a2d9e47; canPushToQuickBooks: s0rolegate-4c8a1f63
import { 
    ChevronLeft, 
    Edit, 
    Send, 
    MoreVertical, 
    Download, 
    Printer, 
    CreditCard, 
    ArrowUpRight, 
    Loader2, 
    Archive, // qbguard-62b8f0a4 - "In QuickBooks" origin badge icon
    CheckCircle,
    XCircle, // qbdetailfail-3f9a1c7e - red push-failure banner icon; matches list Failed badge
    AlertTriangle,
    RotateCcw, // sdrefund3-invrow-7c2d9f41 - refund action icon
    Bell,      // sdrefund3-rem-3b6e8a29 - resume-reminders button icon
    BellOff,   // sdrefund3-rem-3b6e8a29 - reminders-paused banner icon
    Clock,     // sdrefund5-5c1e9b28 - refund-pending banner icon
    MessageSquare, // invnotes-icons-8c3e5b0a - Notes & Media strip header icon
    ChevronDown,   // invnotes-icons-8c3e5b0a - strip collapse chevron
    ChevronUp,     // invnotes-icons-8c3e5b0a - strip expand chevron
    Lock           // impro-3c8f1a57 - "Imported — view only" in place of Edit
} from 'lucide-react';
import { formatCurrency, formatDate, getInvoicePaidDate, normalizePaidDate, isQuickBooksOriginInvoice, isImportedInvoice, isPaymentSyncWarningActive, deriveInvoiceRowFields, toJsDate } from '../utils/formatters'; // r3-paiddate qbguard-a90d5e17 paysyncimp-2c6a9f38 qbdetailfail-b8d2e514 recinv-billperiod-7c1f9a04 impro-3c8f1a57
import InvoiceStatusBadge from '../components/common/InvoiceStatusBadge';
import { isBalanceReviewActive } from '../utils/invoiceBalanceReview'; // s0bcharge-bc5e9925 — paused banner only while the figures disagree
import { isInvoiceClosed } from '../utils/invoiceOwing'; // x2bweb-99626d17 — Apply Credit follows the server's credit refusal
import PaymentModal from '../components/common/PaymentModal';
import RefundModal from '../components/common/RefundModal'; // sdrefund3-invrow-7c2d9f41 - shared refund form
import { isRefundFlowOn, refundRowAction, invoiceCreditView, paymentRowView, DEPOSIT_HINT } from '../utils/invoiceRefundUi'; // refundscreen-7c3e5a18 (R1d-1)
import { RefundQbStatus, CreditNoteLine } from '../components/common/InvoiceRefundStatus'; // refundstatus-8a3d6f12 (R1d-3) - a refund's QuickBooks status + its credit note's email status
import { hasNewFlowRefund, creditNoteIdsOf } from '../utils/invoiceRefundStatus'; // refundstatus-8a3d6f12
import { canRefundInvoice } from '../utils/permissions'; // refundstatus-8a3d6f12 - who may read refund records (Owner / Admin / Manager)
import { addressLines } from '../utils/letterhead'; // invhead-2d7c5b94 - the business address, as on the PDFs
import { downloadPdfBytes } from '../utils/pdfDownload'; // docpdf-8a4c1e73 — the PDF arrives as bytes; nothing stored, no link
import { qbTotalMismatchOf, qbTotalMismatchText, withRecheckAnswer, QB_RECHECK_WORDS } from '../utils/qbTotalMismatch'; // qbpushcheck-7e2c5a94 (row 31 T1) - QuickBooks holds a different total/tax than was billed; qbrecheck-3a7d9e52 - "Check again"
import { canRecheckQuickBooksAmount } from '../utils/permissions'; // qbrecheck-3a7d9e52 - who is offered "Check again"
import TaxExemptionBanner, { withExemptionRefresh } from '../components/common/TaxExemptionBanner'; // taxexempt3-8c4f2a1d — withExemptionRefresh: the server's new figures right after a switch-off
import { lineTaxCheckOf, LINE_TAX_CHECK_TEXT } from '../utils/lineTaxCheck'; // taxlines1-f2503245 - a line's QuickBooks tax code couldn't be checked
import { pickQbFields, withFreshQb } from '../utils/qbFreshFields'; // qbfresh-3d6a8f15 - the page re-reads the QuickBooks sync fields after an export or a re-check
import { reconcileTaxRows } from '../utils/invoiceTaxRows'; // taxrow-4c8e1a72 - the tax rows never contradict the invoice's stored tax
import { customerTaxRows, taxRowText } from '../utils/lineTax'; // taxlines2-3e0b16a4; taxexempt3-8c4f2a1d — taxRowText
import InvoiceRefundDialog from '../components/common/InvoiceRefundDialog'; // refunddialog-4e9b7c25 (R1d-2) - the new refund flow's dialog
import ApplyCreditModal from '../components/common/ApplyCreditModal'; // Seam 4a: apply an account credit to this invoice
import ConfirmationModal from '../components/common/ConfirmationModal'; // creditunapply-2f6b8d41: unapply confirm
import { getQuickBooksMappingOptionsCached } from '../utils/qbMappingCache'; // qbmapcache-4d7e9a2c
import { formatDisplayAddress, buildAddressString } from '../utils/address'; // marker e5a91c73 + a1d4f8b2 -- buildAddressString formats the billTo block (address1/address2 keys)
import { normalizeBillTo, BILLTO_MODES } from '../utils/billTo'; // marker a1d4f8b2 -- Seam 2: resolve/gate the bill-to party on-screen
import useOrgNotes from '../hooks/useOrgNotes'; // invnotes-imp-4d9a2f71 -- live working-record notes strip (same toolkit as quotes/jobs)
import NoteComposer from '../components/crm/NoteComposer'; // invnotes-imp-4d9a2f71
import NoteCard from '../components/crm/NoteCard'; // invnotes-imp-4d9a2f71
import { canOrgTakeOnlinePayments } from '../utils/orgPayments'; // payhide-7e4c1b05 -- hides the staff "Pay Online" tab

// --- ADDED onOpenCustomer to props ---

// s0bunrec-1ef97499 — why a received card payment was left off the invoice. Mirrors
// functions/utils/invoiceChargeable.js UNRECORDED_REASON_TEXT (server writes the code).
const UNRECORDED_REASON_WORDS = {
    closed: 'the invoice was already paid or closed',
    'nothing-owing': 'the invoice had nothing left to pay',
    'exceeds-balance': 'the payment was more than the balance owing',
    'missing-balance': "the invoice's saved balance was missing",
    'balance-mismatch': "the invoice's saved balance did not match its payments",
};

const InvoiceDetailsView = ({ invoice: invoiceProp, companySettings, onBack, onEditInvoice, currentUserProfile, onOpenCustomer, openRecordPayment = false, onRecordPaymentOpened }) => { // h1s6-8b2f4d19: openRecordPayment
    // qbfresh-3d6a8f15 -- the invoice arrives once, as a prop, so after "Export to QB" the page kept
    // showing what it was opened with (a red "push failed" banner from an earlier failed export
    // stayed up after a later one succeeded). After an export or a "Check again" the QuickBooks
    // sync fields are re-read and laid over the prop; everything below reads `invoice` as before.
    const [qbFresh, setQbFresh] = useState(null); // { id, fields } from the last re-read
    const invoice = useMemo(() => withFreshQb(invoiceProp, qbFresh), [invoiceProp, qbFresh]);
    const refreshQbFields = async (fallbackFields) => {
        const orgId = currentUserProfile?.organizationId;
        if (!invoiceProp?.id || !orgId) return;
        try {
            const snap = await getDoc(doc(db, `organizations/${orgId}/invoices`, invoiceProp.id));
            if (snap.exists()) { setQbFresh({ id: invoiceProp.id, fields: pickQbFields(snap.data()) }); return; }
        } catch (e) {
            console.error('Could not re-read the invoice after a QuickBooks action:', e);
        }
        if (fallbackFields) setQbFresh({ id: invoiceProp.id, fields: fallbackFields }); // what we know for certain
    };
    const navigate = useNavigate(); // invqlink-b7d2e9c4 -- deep-link the Estimate # to its quote (parent loads the full doc from /quotes/:id)
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    // h1s6-8b2f4d19 — H1 Seam 6: the editor's "Save & Record Payment" lands here and opens the form once.
    useEffect(() => {
        if (!openRecordPayment) return;
        onRecordPaymentOpened?.();
        if (canRecordPayment(currentUserProfile) && invoiceProp?.status !== 'Paid') setIsPaymentModalOpen(true);
    }, [openRecordPayment]); // eslint-disable-line react-hooks/exhaustive-deps
    const [isApplyCreditOpen, setIsApplyCreditOpen] = useState(false); // Seam 4a
    const [customerCredits, setCustomerCredits] = useState([]); // Seam 4a: this customer's credits (button visibility)
    // sdrefund3-invrow-7c2d9f41 -- refund a specific payment row (RefundModal) and
    // sdrefund3-rem-3b6e8a29 -- resume reminders a refund muted.
    const [refundTarget, setRefundTarget] = useState(null); // { payment, idx } | null
    // refunddialog-4e9b7c25 -- R1d-2. A company WITH the new flow opens the "Why are you refunding?"
    // dialog; one without it sets refundTarget and gets the old form below, exactly as before.
    const [refundDialogTarget, setRefundDialogTarget] = useState(null);
    const [unapplyTarget, setUnapplyTarget] = useState(null); // creditunapply-2f6b8d41: { payment, idx } | null
    const [unapplyBusy, setUnapplyBusy] = useState(false);
    const [reminderBusy, setReminderBusy] = useState(false);

    // Loading states for actions
    const [isExporting, setIsExporting] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // Inline feedback banner — replaces all alert() / confirm() calls
    const [actionFeedback, setActionFeedback] = useState(null); // { type: 'success'|'error', message: string }
    const [confirmSend, setConfirmSend] = useState(false); // inline confirmation instead of window.confirm
    // custcopy-32471cbd 8d — the job's customer-ready checklists offered in the send banner (office roles), and the ticks.
    const [attachChoices, setAttachChoices] = useState([]);
    const [attachTicks, setAttachTicks] = useState({});

    const showFeedback = (type, message) => {
        setActionFeedback({ type, message });
        setTimeout(() => setActionFeedback(null), 5000);
    };
    
    const [taxOptions, setTaxOptions] = useState([]);

    // paysyncwire-9a2f6c84 -- payment-sync ack state. Declared at the top with the other
    // hooks (before any early return) so rules-of-hooks holds. The optimistic overlay and
    // handlers that use these live further down, just before the main return.
    const [ackOverlay, setAckOverlay] = useState(null);
    const [confirmDismiss, setConfirmDismiss] = useState(false);
    const [isDismissing, setIsDismissing] = useState(false);
    useEffect(() => { setAckOverlay(null); setConfirmDismiss(false); }, [invoice?.id]);
    // qbrecheck-3a7d9e52 -- "Check again" on the QuickBooks amount banner: the server's last answer
    // for THIS invoice (laid over the prop further down), and whether a check is running.
    const [recheckAnswer, setRecheckAnswer] = useState(null);
    const [isRechecking, setIsRechecking] = useState(false);
    useEffect(() => { setRecheckAnswer(null); }, [invoice?.id]);
    // recheckmsg-7a4c2e96 -- "Check again" found QuickBooks STILL differs: an amber notice that stays
    // until dismissed (it used to ride the green message strip, which clears itself on a timer).
    const [recheckNotice, setRecheckNotice] = useState(null);
    useEffect(() => { setRecheckNotice(null); }, [invoice?.id]);

    // impayrows-3d84c6f1 -- READ-ONLY subscription to this invoice's imported
    // payments (customers/{cid}/importedPayments where invoiceId == this invoice's
    // doc id — single-field equality, no composite index). This file has no live
    // subscription of its own (the invoice arrives as a prop), so the
    // onSnapshot+unsubscribe pattern mirrors CustomerFormModal's importedPayments
    // subscription. Missing customerId → no query, treated as empty; snapshot
    // errors → empty. Nothing in the view blocks on this.
    const [importedInvoicePayments, setImportedInvoicePayments] = useState([]);
    // refundstatus-8a3d6f12 -- R1d-3. Read-only listeners for what the new refund flow recorded:
    //   refundRecords   this invoice's refund records (QuickBooks status). Only Owner / Admin / Manager
    //                   may read them (firestore.rules), and only a company WITH the flow has any, so
    //                   the listener starts only then, and only if the invoice carries such a refund.
    //   creditNoteDocs  this invoice's credit notes (email status); any member of the company may read
    //                   them. Starts only when the invoice has a credit note.
    // An invoice with neither (every invoice of a company without the flow) opens no listener.
    const [refundRecords, setRefundRecords] = useState({});
    const [creditNoteDocs, setCreditNoteDocs] = useState({});
    const refundStatusOrgId = currentUserProfile?.organizationId;
    const refundStatusOn = isRefundFlowOn(companySettings) && canRefundInvoice(currentUserProfile) && hasNewFlowRefund(invoice);
    const creditNoteIdsKey = creditNoteIdsOf(invoice).join(',');
    useEffect(() => {
        setRefundRecords({});
        if (!refundStatusOn || !refundStatusOrgId || !invoice?.id) return undefined;
        return onSnapshot(
            collection(db, `organizations/${refundStatusOrgId}/invoices/${invoice.id}/refunds`),
            (snap) => { const next = {}; snap.forEach((d) => { next[d.id] = d.data(); }); setRefundRecords(next); },
            () => setRefundRecords({}),
        );
    }, [refundStatusOn, refundStatusOrgId, invoice?.id]);
    useEffect(() => {
        setCreditNoteDocs({});
        if (!creditNoteIdsKey || !refundStatusOrgId) return undefined;
        const unsubs = creditNoteIdsKey.split(',').map((id) => onSnapshot(
            doc(db, `organizations/${refundStatusOrgId}/creditNotes/${id}`),
            (d) => setCreditNoteDocs((prev) => ({ ...prev, [id]: d.exists() ? d.data() : null })),
            () => {},
        ));
        return () => unsubs.forEach((u) => u());
    }, [creditNoteIdsKey, refundStatusOrgId]);
    useEffect(() => {
        setImportedInvoicePayments([]);
        const orgId = currentUserProfile?.organizationId;
        const cid = invoice?.customerId;
        const invId = invoice?.id;
        if (!orgId || !cid || !invId) return;
        const importedPaymentsQ = query(
            collection(db, `organizations/${orgId}/customers/${cid}/importedPayments`),
            where('invoiceId', '==', invId)
        );
        const unsub = onSnapshot(
            importedPaymentsQ,
            s => setImportedInvoicePayments(s.docs.map(d => ({ id: d.id, ...d.data() }))),
            () => setImportedInvoicePayments([])
        );
        return unsub;
    }, [currentUserProfile?.organizationId, invoice?.customerId, invoice?.id]);

    // Seam 4a -- READ-ONLY subscription to this customer's account credits
    // (customerCredits where customerId == this invoice's customer — single-field
    // equality, no composite index; rules allow any org member to read). Drives
    // whether the "Apply Credit" button appears (only when at least one open,
    // positive-balance credit not yet applied to THIS invoice exists). Errors →
    // empty → button simply doesn't show. Mirrors the importedInvoicePayments sub.
    useEffect(() => {
        setCustomerCredits([]);
        const orgId = currentUserProfile?.organizationId;
        const cid = invoice?.customerId;
        if (!orgId || !cid) return;
        const creditsQ = query(
            collection(db, `organizations/${orgId}/customerCredits`),
            where('customerId', '==', cid)
        );
        const unsub = onSnapshot(
            creditsQ,
            s => setCustomerCredits(s.docs.map(d => ({ id: d.id, ...d.data() }))),
            () => setCustomerCredits([])
        );
        return unsub;
    }, [currentUserProfile?.organizationId, invoice?.customerId]);

    // invnotes-state-6f1d7a94 -- working-record Notes & Media strip (added alongside
    // the customer-facing preview below, not in place of it). A note added from an
    // invoice must land where mobile lands it: on the LINKED JOB when there is one,
    // and on the invoice only when there isn't. Resolve the job the same way the
    // server resolver and mobile do (invoice.linkedJobId || invoice.jobId), then key
    // the live list + the composer target off that. On the detail view invoice.id
    // always exists, so the standalone branch always has a valid invoice: key.
    const notesJobId = invoice?.linkedJobId || invoice?.jobId || null;

    // custcopy-32471cbd 8d — when the send banner opens for an office role, list the invoice's job's
    // checklists that are not removed and whose template has "Customers can receive a copy" on. One read
    // each time the banner opens (no listener); nothing shows when none qualify. Every tick is re-checked
    // by the server (sendInvoiceEmail), so this list only decides what to OFFER.
    const canAttachChecklists = isChecklistOfficeRole(currentUserProfile);
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!confirmSend || !canAttachChecklists || !orgId || !notesJobId) { setAttachChoices([]); setAttachTicks({}); return undefined; }
        let live = true;
        Promise.all([
            getDocs(collection(db, `organizations/${orgId}/jobs/${notesJobId}/checklists`)),
            getDocs(collection(db, `organizations/${orgId}/checklistTemplates`)),
        ]).then(([copies, templates]) => {
            if (!live) return;
            setAttachChoices(invoiceChecklistChoices(
                copies.docs.map(d => ({ id: d.id, ...d.data() })),
                templates.docs.map(d => ({ id: d.id, ...d.data() })),
            ));
        }).catch(() => { if (live) setAttachChoices([]); }); // the invoice still sends; just nothing to attach
        return () => { live = false; };
    }, [confirmSend, canAttachChecklists, currentUserProfile?.organizationId, notesJobId]);
    const toggleAttach = (c, on) => {
        if (on && c.status !== 'complete' && !window.confirm(CHECKLIST_UNFINISHED_ASK)) return;
        setAttachTicks(prev => ({ ...prev, [c.id]: on }));
    };
    const notesAttachedKey = notesJobId
        ? `job:${notesJobId}`
        : (invoice?.id ? `invoice:${invoice.id}` : null);
    const notesAttachedTo = notesJobId
        ? [{ type: 'job', id: notesJobId }]
        : (invoice?.id ? [{ type: 'invoice', id: invoice.id }] : []);
    const [notesExpanded, setNotesExpanded] = useState(false);
    const { notes: invoiceNotes } = useOrgNotes({ orgId: currentUserProfile?.organizationId, attachedKey: notesAttachedKey });

    // r1-3m-opnotes-state-4b8e1d6a -- operator-facing customer-notes section.
    // The raw invoice prop carries only includedNoteIds / includeLegacyTechNotes /
    // linkedJobId; the resolved note text and SIGNED photo URLs are built server-
    // side, so we fetch them via getOperatorInvoiceNotes (operator twin of the
    // portal's getCustomerQuoteNotes). jobNotes carry /notes ids so Remove can
    // shorten includedNoteIds. Read-only fetch; nothing in the view blocks on it.
    const [opNotes, setOpNotes] = useState(null); // { items, jobNotes, includedNoteIds, hasInclusionList }
    const [opNotesLoading, setOpNotesLoading] = useState(false);
    const [removingNoteId, setRemovingNoteId] = useState(null);
    useEffect(() => {
        let alive = true;
        setOpNotes(null);
        const invId = invoice?.id;
        if (!invId) return;
        setOpNotesLoading(true);
        httpsCallable(functions, 'getOperatorInvoiceNotes')({ invoiceId: invId })
            .then((res) => { if (alive) setOpNotes(res.data); })
            .catch((e) => { console.error('getOperatorInvoiceNotes failed:', e); if (alive) setOpNotes(null); })
            .finally(() => { if (alive) setOpNotesLoading(false); });
        return () => { alive = false; };
    }, [invoice?.id]);

    // jobnum-3e8c1f92 -- THE JOB # A USER SEES.
    //
    // Three writers disagreed about linkedJobNumber, so production carries three shapes
    // across 43 job-linked invoices:
    //   9  a real job number        (batchCreateInvoices)
    //   14 an internal DOC ID       (web createNewInvoiceFromSource) -- printed raw to the
    //      user, and on a recurring visit long enough to overflow the next column
    //   20 nothing at all           (completeJob wrote the number as `jobNumber`)
    // The writers are fixed, but those 43 invoices are already stored and there are no
    // production writes in this change, so the DISPLAY has to cope with all three
    // forever. It resolves in three steps, cheapest first, and never prints a doc id.
    //
    // Step 1/2 need no reads: the stored linkedJobNumber if it actually looks like a
    // number, else the invoice's own `jobNumber` field (completeJob's name), which covers
    // all 20 of the missing ones for free. Step 3 reads the job doc, and runs ONLY for a
    // stored doc id -- so it is the 14, once each, and never for a correctly-written
    // invoice. A deleted job resolves to nothing and the link degrades to "View job".
    //
    // The predicate is deliberately a positive test for a job NUMBER, not a guess at what
    // a doc id looks like: numbers here are short ('0042', '1795') or a short imported
    // code, while a Firestore auto-id is 20 chars and a recurring occurrence id is
    // `job_<20>_<8>` -- so a 12-char cap plus rejecting the `job_` prefix separates them
    // with room to spare, and anything unrecognised is treated as NOT a number, which
    // fails safe (a link with no number beats a raw id).
    const looksLikeJobNumber = (v) => {
        if (typeof v !== 'string') return false;
        const s = v.trim();
        if (s === '' || s.length > 12) return false;
        if (/^job_/i.test(s)) return false;
        return /^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(s);
    };

    const linkedJobIdForNumber = invoice?.linkedJobId || invoice?.jobId || null;
    const storedJobNumber = looksLikeJobNumber(invoice?.linkedJobNumber)
        ? invoice.linkedJobNumber.trim()
        : (looksLikeJobNumber(invoice?.jobNumber) ? invoice.jobNumber.trim() : null);
    const [fetchedJobNumber, setFetchedJobNumber] = useState(null);
    useEffect(() => {
        let alive = true;
        setFetchedJobNumber(null);
        const orgId = currentUserProfile?.organizationId;
        // Only when steps 1 and 2 failed AND there is a job to ask. Read-only; a failure
        // (deleted job, no access) leaves the label as the plain "View job" fallback.
        if (!orgId || !linkedJobIdForNumber || storedJobNumber) return undefined;
        getDoc(doc(db, `organizations/${orgId}/jobs`, linkedJobIdForNumber))
            .then((snap) => {
                if (!alive || !snap.exists()) return;
                const n = snap.data()?.jobNumber;
                if (looksLikeJobNumber(n)) setFetchedJobNumber(String(n).trim());
            })
            .catch((e) => { console.warn('jobnum-3e8c1f92: could not resolve job number', e); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedJobIdForNumber, storedJobNumber, currentUserProfile?.organizationId]);

    const displayJobNumber = storedJobNumber || fetchedJobNumber || null;
    // Render the block whenever there is a job to reach OR a number to show. The old gate
    // was `invoice.linkedJobNumber &&`, which hid the Job # entirely on all 20 of the
    // completeJob invoices even though they carry a perfectly good link.
    const showJobNumberBlock = !!(linkedJobIdForNumber || displayJobNumber);

    // r1-3m-opnotes-remove-6d1a8b3f -- Remove a note from THIS invoice only. Edits
    // invoice.includedNoteIds (the curated set the PDF/portal read); it does NOT
    // delete the /notes doc -- that is NoteCard's trash, a different control. For an
    // invoice with no includedNoteIds yet (every pre-r1-3d invoice), all job notes
    // currently flow, so we MATERIALIZE the full id list first, then drop one. Write
    // goes straight to the invoice doc (firestore.rules invoices update rule:
    // isUserInOrg(orgId) && ownsCustomer(resource.data.customerId) && billToLockSafe()
    // -- billTo unchanged, so billToLockSafe passes; ownsCustomer gates non-owners).
    const handleRemoveInvoiceNote = async (noteId) => {
        if (!invoice?.id || !currentUserProfile?.organizationId || !opNotes) return;
        // MATERIALIZE: with an explicit list, start from it; without one (the invoice
        // auto-includes everything), start from the FULL job-note id set the callable
        // returned. Either way, drop just this id -> a concrete, shorter array.
        const currentIds = opNotes.hasInclusionList
            ? (opNotes.includedNoteIds || [])
            : (opNotes.jobNotes || []).map((n) => n.id);
        const newIds = currentIds.filter((id) => id !== noteId);
        setRemovingNoteId(noteId);
        try {
            const ref = doc(db, `organizations/${currentUserProfile.organizationId}/invoices`, invoice.id);
            await updateDoc(ref, { includedNoteIds: newIds });
            showFeedback('success', 'Removed from this invoice. The note itself was not deleted.');
            // Re-fetch so both the faithful display and the curation rows reflect the write.
            try {
                const res = await httpsCallable(functions, 'getOperatorInvoiceNotes')({ invoiceId: invoice.id });
                setOpNotes(res.data);
            } catch (_) { /* display stays as-is; next open refreshes */ }
        } catch (e) {
            console.error('Remove invoice note failed:', e);
            showFeedback('error', "Could not remove. You may not have access to this customer's invoices.");
        } finally {
            setRemovingNoteId(null);
        }
    };

    const menuRef = useRef(null);
    const invoiceRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuRef]);

    // qbmapcache-4d7e9a2c -- Only fetch QB tax codes when this org actually has
    // QuickBooks connected, and reuse a per-org session cache. companySettings
    // starts as {} in CrmApp and arrives async via onSnapshot, so qbConnected is
    // in the dep array: without it the effect (deps were [currentUserProfile])
    // would run once while the flag is still undefined, gate out, and never
    // re-run -- leaving taxOptions permanently empty and silently breaking the
    // legacy-invoice tax fallback. Hoisted to a plain variable so it is not a
    // complex expression in the dep array (react-hooks/exhaustive-deps).
    const qbConnected = companySettings?.quickbooksConnected;
    useEffect(() => {
        const fetchTaxCodes = async () => {
            if (!currentUserProfile?.organizationId) return;
            if (!qbConnected) return;
            try {
                const data = await getQuickBooksMappingOptionsCached(currentUserProfile.organizationId);
                if (data?.taxCodes) {
                    setTaxOptions(data.taxCodes);
                }
            } catch (error) {
                console.error("Failed to load tax codes for view:", error);
            }
        };
        fetchTaxCodes();
    }, [currentUserProfile, qbConnected]);
    
    const taxBreakdown = useMemo(() => {
        if (!invoice?.lineItems) return {};

        // ROUND 3c VERIFIER: 1761744000-round3c-fixB-tax-shortcircuit
        // Imported invoices lump tax into total by design (multi-jurisdiction
        // safety — see Round 3a buildImportedInvoiceBody). Returning empty
        // breakdown here means the UI shows Subtotal = Total with no tax rows,
        // matching the data model. Without this short-circuit, the tax calc
        // would default rate=0 and emit a confusing "Tax (0%): $0.00" row.
        if (invoice.source === 'import') return {};

        // taxlines2-3e0b16a4 — the invoice's stored tax parts, one row each, when they add up to its tax
        // (the same rows the PDF, email and portal show); otherwise the rows below, as before.
        const partRows = customerTaxRows(invoice, 'headline');
        if (partRows) return partRows.reduce((acc, r) => { const k = taxRowText(r); acc[k] = (acc[k] || 0) + r.amount; return acc; }, {}); // taxexempt3-8c4f2a1d — an exempted part keeps its own "— exempt #…" row

        // taxrow-4c8e1a72 -- the rows are built from each line's label as before, then checked
        // against the tax stored on the invoice (what the Grand Total, PDF, email and portal show).
        return reconcileTaxRows(invoice.lineItems.reduce((acc, item) => {
            if (item.taxExempt) return acc;

            let rate = item.taxSnapshot?.rate;
            let name = item.taxSnapshot?.name;

            if (rate === undefined || rate === null) {
                const matchedCode = taxOptions.find(t => t.Id === item.taxCodeId);
                
                if (matchedCode) {
                    rate = matchedCode.Rate;
                    name = matchedCode.Name;
                } 
                else if (!item.taxCodeId && companySettings?.quickbooksSettings?.defaultTaxCodeId) {
                    const defaultCode = taxOptions.find(t => t.Id === companySettings.quickbooksSettings.defaultTaxCodeId);
                    if (defaultCode) {
                        rate = defaultCode.Rate;
                        name = defaultCode.Name;
                    }
                }
            }

            if (rate === undefined || rate === null) {
                rate = companySettings?.taxRate || 0;
                name = `Tax`;
            }

            if (!name) name = 'Tax';
            if (!name.includes('%') && rate > 0) {
                name = `${name} (${rate}%)`;
            } else if (rate === 0 && !name.toLowerCase().includes('zero')) {
                 name = `${name} (0%)`;
            }

            // ROUND 3c VERIFIER: 1761744000-round3c-fixA-item-shape
            // Live Spatchy line items use `price`, imported items use `unitPrice`.
            // Read both with nullish coalescing.
            const itemPrice = item.price ?? item.unitPrice ?? 0;
            const amount = (parseFloat(item.quantity || 0) * parseFloat(itemPrice)) * (rate / 100);

            if (!acc[name]) acc[name] = 0;
            acc[name] += amount;
            return acc;
        }, {}), invoice);
    }, [invoice, companySettings, taxOptions]);

    if (!invoice) {
        return null;
    }

    // ROUND 3c VERIFIER: 1761744000-round3c-fix0-address
    // Imported invoices have customerDetails.address as an OBJECT
    // ({line1, line2, city, state, zip, country}), while live Spatchy
    // invoices have it as a string. Rendering an object directly in JSX
    // throws "Objects are not valid as a React child" — the red-screen-of-
    // death when clicking imported invoices. This helper handles both shapes.
    // marker d2f6b8e1 -- delegate to the shared formatter so admin + portal share
    // ONE object-tolerant address renderer and can't drift. Call sites below
    // (formatAddress(...)) are unchanged.
    const formatAddress = formatDisplayAddress;

    const customer = invoice.customerDetails;
    // marker b7e2c9a5 -- Seam 2: resolve the bill-to party once (mirror of the
    // server generateInvoiceHtml). null (absent/malformed) => no BILL TO block.
    // billTo address uses buildAddressString (address1/address2 keys), suppressed
    // for a propertyContact when propertyId is set (already under Service Location).
    const billTo = normalizeBillTo(invoice.billTo);
    const billToAddress = (billTo && !(billTo.mode === BILLTO_MODES.PROPERTY_CONTACT && invoice.propertyId))
        ? buildAddressString(billTo)
        : '';
    const currencyCode = companySettings?.currency || 'USD';

    const handleExportToQuickBooks = async () => {
        if (!invoice.id) return;
        setIsExporting(true);
        try {
            // qbcto-3e7f1a9c -- wait longer than the server can run (90s). Without
            // this the SDK's 70s default aborts a slow-but-succeeding push, showing
            // the operator an error while QuickBooks was actually written. Ceiling
            // only -- a fast push still resolves the instant the server returns.
            const exportFn = httpsCallable(functions, 'exportInvoiceToQuickBooks', { timeout: 120000 });
            const exported = await exportFn({ invoiceId: invoice.id });
            showFeedback('success', 'Invoice exported to QuickBooks successfully.');
            // qbfresh-3d6a8f15 -- it is in QuickBooks now: clear any earlier "push failed" banner, show it as synced.
            await refreshQbFields(exported?.data?.quickbooksInvoiceId ? { qbInvoiceId: exported.data.quickbooksInvoiceId } : null);
        } catch (error) {
            console.error("Export failed:", error);
            showFeedback('error', `Export to QuickBooks failed: ${error.message}`);
            await refreshQbFields(null); // qbfresh-3d6a8f15 -- show the failure the server just saved on the invoice
        } finally {
            setIsExporting(false);
        }
    };

    const handleSendEmail = async () => {
        if (!invoice.id) return;
        
        const customerEmail = invoice.customerDetails?.email;
        if (!customerEmail) {
            showFeedback('error', 'This customer does not have an email address saved. Please edit the customer record and add one first.');
            return;
        }

        // Show inline confirmation instead of window.confirm
        setConfirmSend(true);
    };

    const handleSendEmailConfirmed = async () => {
        const customerEmail = invoice.customerDetails?.email;
        // custcopy-32471cbd 8d — only the ticked checklists (unfinished ones were confirmed when ticked);
        // nothing ticked = exactly today's request, { invoiceId } alone.
        const picks = attachChoices.filter(c => attachTicks[c.id]).map(c => ({ checklistId: c.id, ...(c.status !== 'complete' ? { sendUnfinished: true } : {}) }));
        setConfirmSend(false);
        setIsSending(true);
        try {
            const sendEmail = httpsCallable(functions, 'sendInvoiceEmail', picks.length ? { timeout: 120000 } : undefined);
            await sendEmail(picks.length ? { invoiceId: invoice.id, attachChecklists: picks } : { invoiceId: invoice.id });
            showFeedback('success', picks.length
                ? `Invoice emailed successfully to ${customerEmail}, with ${picks.length} checklist${picks.length === 1 ? '' : 's'} attached.`
                : `Invoice emailed successfully to ${customerEmail}.`);
        } catch (error) {
            console.error("Failed to send email:", error);
            showFeedback('error', `Failed to send email: ${error.message}`);
        } finally {
            setIsSending(false);
        }
    };

    const handlePrint = () => {
        window.print();
        setIsMenuOpen(false);
    };

    // r1-3l-invoicepdfbtn-a7f4c2e9 — call the backend downloadInvoicePdf callable and
    // navigate to the returned signed URL, matching QuoteDetailsView's handleDownloadPdf
    // (r1-p6-qdvpdfbtn-6b1e7d28 / r1-p6-nofetch-4f1c8d90). The backend renders the same
    // template the customer's portal PDF uses, so notes & photos appear; the old
    // client-side canvas screenshot could not render signed Storage photos.
    // docpdf-8a4c1e73 — getInvoicePdf returns the PDF's bytes (nothing stored, no link); the file is saved here.
    const handleDownloadPdf = async () => {
        if (!invoice?.id) return;
        setIsMenuOpen(false);
        try {
            const result = await httpsCallable(functions, 'getInvoicePdf', { timeout: 120000 })({ invoiceId: invoice.id });
            downloadPdfBytes(result.data?.pdfBase64, result.data?.fileName);
        } catch (error) {
            console.error('Invoice PDF download failed:', error);
            showFeedback('error', `Failed to download PDF: ${error.message}`);
        }
    };

    // paysyncwire-9a2f6c84 -- persisted payment-sync acknowledgement. State + effect are
    // declared at the top with the other hooks (rules-of-hooks); the overlay is applied
    // here so a successful write clears the banner without a live onSnapshot on the prop.
    // Both writes use updateDoc, where deleteField is legal; neither touches setDoc.
    const paySyncInvoice = ackOverlay ? { ...invoice, ...ackOverlay } : invoice;
    const paySyncConnected = !!companySettings?.quickbooksConnected;
    const showPaySyncWarning = paySyncConnected && isPaymentSyncWarningActive(paySyncInvoice);
    const paySyncHandled = paySyncConnected && !!paySyncInvoice?.qbPaymentSyncError && !isPaymentSyncWarningActive(paySyncInvoice);
    const canDismissPaySync = can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS);

    // qbdetailfail-c07af6d1 -- read-only push-failure flag. Derived via the SAME
    // helper the invoice list uses (deriveInvoiceRowFields), so the list's red
    // "Failed" badge and this banner can never disagree about what "failed" means.
    // The returned object is discarded (only the boolean is kept), so nothing is
    // attached to `invoice` and no derived key rides into the edit-save write.
    const showQbPushFailed = paySyncConnected && deriveInvoiceRowFields(invoice.id, invoice).qbPushState === 'failed';

    // qbdefer-2d6b8e50 -- Seam A: read-only "waiting to sync" flag, the detail-page
    // twin of the list's amber "Waiting to sync" badge. Same shared helper as the
    // failed flag above, so list and detail agree on what "deferred" means. Mutually
    // exclusive with showQbPushFailed by the helper's precedence (failed outranks
    // deferred). Degrades to false when qbAutoSyncDeferred is absent -- every invoice
    // predating auto-sync.
    const showQbPushDeferred = paySyncConnected && deriveInvoiceRowFields(invoice.id, invoice).qbPushState === 'deferred';

    // qbpushcheck-7e2c5a94 -- row 31 T1, read-only: the push recorded that QuickBooks holds a
    // different total or tax than this invoice. null for every invoice without the marker.
    const qbTotalMismatch = paySyncConnected ? qbTotalMismatchOf(withRecheckAnswer(invoice, recheckAnswer)) : null;
    const qbTotalMismatchWords = qbTotalMismatch ? qbTotalMismatchText(qbTotalMismatch, (n) => formatCurrency(n, currencyCode)) : null;

    // qbrecheck-3a7d9e52 -- "Check again": the server re-reads this invoice from QuickBooks (it sends
    // QuickBooks nothing), then clears the warning or refreshes its figures. The server is the gate;
    // canRecheckQuickBooksAmount only decides whether the button is offered.
    const handleRecheckQbTotal = async () => {
        if (!invoice?.id || isRechecking) return;
        setIsRechecking(true);
        setRecheckNotice(null); // recheckmsg-7a4c2e96 -- a new check replaces the last answer
        try {
            const res = await httpsCallable(functions, 'recheckQuickBooksInvoiceTotal')({ invoiceId: invoice.id });
            const answer = res.data || {};
            setRecheckAnswer(answer);
            // recheckmsg-7a4c2e96 -- "now matches" stays green and timed; "still differs" is amber and stays.
            if (answer.matches) showFeedback('success', QB_RECHECK_WORDS.nowMatches);
            else setRecheckNotice(QB_RECHECK_WORDS.stillDiffers);
            await refreshQbFields(null); // qbfresh-3d6a8f15 -- a successful re-check also clears a stale "push failed" banner
        } catch (e) {
            console.error('QuickBooks re-check failed:', e);
            showFeedback('error', e?.message && e.message !== 'internal' ? e.message : QB_RECHECK_WORDS.failed);
        } finally {
            setIsRechecking(false);
        }
    };

    const handleDismissPaySync = async () => {
        if (!invoice?.id || !currentUserProfile?.organizationId) return;
        setIsDismissing(true);
        setConfirmDismiss(false);
        const optimistic = {
            qbPaymentSyncAckedErrorAt: invoice.qbPaymentSyncErrorAt,
            qbPaymentSyncAckAt: new Date().toISOString(),
            qbPaymentSyncAckBy: { uid: currentUserProfile.id, name: currentUserProfile.name || "Unknown" },
        };
        setAckOverlay(optimistic);
        try {
            const ref = doc(db, `organizations/${currentUserProfile.organizationId}/invoices`, invoice.id);
            await updateDoc(ref, optimistic);
            showFeedback("success", "Marked as handled. This did not send anything to QuickBooks.");
        } catch (e) {
            console.error("Payment-sync dismiss failed:", e);
            setAckOverlay(null);
            showFeedback("error", "Could not save. You may not have access to this customer's invoices.");
        } finally {
            setIsDismissing(false);
        }
    };

    const handleUndoPaySync = async () => {
        if (!invoice?.id || !currentUserProfile?.organizationId) return;
        setIsDismissing(true);
        setAckOverlay({ qbPaymentSyncAckedErrorAt: null, qbPaymentSyncAckAt: null, qbPaymentSyncAckBy: null });
        try {
            const ref = doc(db, `organizations/${currentUserProfile.organizationId}/invoices`, invoice.id);
            await updateDoc(ref, {
                qbPaymentSyncAckedErrorAt: deleteField(),
                qbPaymentSyncAckAt: deleteField(),
                qbPaymentSyncAckBy: deleteField(),
            });
        } catch (e) {
            console.error("Payment-sync undo failed:", e);
            setAckOverlay(null);
            showFeedback("error", "Could not undo. Please try again.");
        } finally {
            setIsDismissing(false);
        }
    };

    // sdrefund3-invrow-7c2d9f41 -- issue a refund of a specific payment. The modal
    // owns the amount/method/cap; here we just call the role-gated callable and
    // refresh so the new refund row + reopened balance appear. The callable throws on
    // failure, which the modal surfaces inline.
    // sdrefund5-1b6d8f30 -- the card path needs the PaymentIntent id of the clicked
    // row (the server refunds THAT specific charge and caps per-payment on it). We
    // send the selected row's stripePaymentIntentId; the server ignores it on the
    // offline path, and rejects a card refund of a payment that has none.
    const handleRefundPayment = async ({ amount, offline, method }) => {
        const refund = httpsCallable(functions, 'refundInvoicePayment');
        await refund({
            invoiceId: invoice.id,
            amount,
            offline,
            method,
            paymentIntentId: refundTarget?.payment?.stripePaymentIntentId,
        });
        window.location.reload();
    };

    // creditunapply-2f6b8d41 -- reverse (unapply) an applied account credit. The server
    // restores the credit's available balance, lowers this invoice's amountPaid via an
    // append-only negative row, and clears creditApplied if it was the last credit here.
    const handleUnapplyCredit = async () => {
        if (!unapplyTarget?.payment) return;
        const { payment } = unapplyTarget;
        setUnapplyBusy(true);
        try {
            await httpsCallable(functions, 'unapplyCreditFromInvoice')({
                creditId: payment.creditId,
                invoiceId: invoice.id,
                creditEntryId: payment.creditEntryId,
            });
            window.location.reload();
        } catch (e) {
            showFeedback('error', e?.message || 'Could not remove the credit. Please try again.');
            setUnapplyBusy(false);
            setUnapplyTarget(null);
        }
    };

    // sdrefund3-rem-3b6e8a29 -- turn scheduled reminders back on for an invoice a
    // refund muted, when the reopened balance is genuinely owed.
    const handleResumeReminders = async () => {
        setReminderBusy(true);
        try {
            await httpsCallable(functions, 'setInvoiceReminders')({ invoiceId: invoice.id, muted: false });
            showFeedback('success', 'Reminders resumed.');
            window.location.reload();
        } catch (e) {
            showFeedback('error', e?.message || 'Could not resume reminders. Please try again.');
            setReminderBusy(false);
        }
    };

    // sdrefund3-invrow-7c2d9f41 -- offline cap MUST mirror the server: at most the
    // invoice's live net amount paid (refundInvoicePayment caps on inv.amountPaid),
    // and never more than the clicked payment. Min of the two is always <= the
    // server cap, so the operator can't even try to exceed it.
    const invoiceNetPaid = Number(invoice.amountPaid) || 0;
    const refundCap = refundTarget
        ? Math.max(0, Math.min(Number(refundTarget.payment?.amount) || 0, invoiceNetPaid))
        : 0;

    // refundscreen-7c3e5a18 -- R1d-1, display only. refundFlowOn reads the server-owned company
    // switch (never written here); companySettings starts as {} so it is false until the company
    // record arrives. creditView is null for every invoice without a credit note, and everything
    // below then renders exactly as before.
    const refundFlowOn = isRefundFlowOn(companySettings);
    const creditView = invoiceCreditView(invoice);

    // Seam 4a -- "Apply Credit" button visibility. Show it only when this operator
    // can manage financials, the invoice still has a balance, and the customer has
    // at least one open, positive-balance credit not already applied to THIS invoice
    // (an applicable credit). Hidden — not disabled — otherwise, to avoid an
    // always-visible-but-inert button in an already-crowded header. Balance is the
    // same Variant-A derivation the footer/PDF use; the server is the real ceiling.
    const round2Credit = (x) => Math.round((Number(x) || 0) * 100) / 100;
    const creditInvoiceBalanceDue = (() => {
        const total = Number(invoice.total) || 0;
        const hist = Array.isArray(invoice.paymentHistory) ? invoice.paymentHistory : [];
        const paid = invoice.amountPaid ?? hist.reduce((s, p) => s + (Number(p && p.amount) || 0), 0);
        let bal = invoice.balanceDue !== undefined ? Number(invoice.balanceDue) : (total - paid);
        if (bal < 0.01) bal = 0;
        return round2Credit(bal);
    })();
    // creditunapply-2f6b8d41 -- entry ids of credit applications that have been reversed
    // (unapplied) on THIS invoice. Used to (a) drop since-unapplied credits from "applied
    // here" so the Apply Credit button reappears for re-application, and (b) hide the
    // per-row Unapply control on an already-reversed credit row.
    const reversedCreditEntryIdsHere = new Set(
        (Array.isArray(invoice.paymentHistory) ? invoice.paymentHistory : [])
            .filter(p => p && p.isCreditReversal && p.reversedCreditEntryId)
            .map(p => p.reversedCreditEntryId)
    );
    const appliedCreditIdsHere = new Set(
        (Array.isArray(invoice.paymentHistory) ? invoice.paymentHistory : [])
            .filter(p => p && p.isCredit && p.creditId && !reversedCreditEntryIdsHere.has(p.creditEntryId))
            .map(p => p.creditId)
    );
    const hasApplicableCredit = customerCredits.some(
        c => c.status === 'open' && round2Credit(c.availableBalance) > 0 && !appliedCreditIdsHere.has(c.id)
    );
    const showApplyCredit = can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS)
        && !isInvoiceClosed(invoice) // x2bweb-99626d17 — X2b: hidden where the server refuses a credit (Paid / Written Off / Void / Cancelled; a Draft still allows it)
        && creditInvoiceBalanceDue > 0
        && hasApplicableCredit;

    return (
        <>
            {refundDialogTarget && refundFlowOn && (
                <InvoiceRefundDialog
                    isOpen
                    onClose={() => setRefundDialogTarget(null)}
                    onDone={() => window.location.reload()}
                    orgId={currentUserProfile?.organizationId}
                    invoice={invoice}
                    clicked={refundDialogTarget}
                    companySettings={companySettings}
                    currencyCode={currencyCode}
                />
            )}
            {refundTarget && (
                <RefundModal
                    isOpen={!!refundTarget}
                    onClose={() => setRefundTarget(null)}
                    title="Refund a payment"
                    subjectLabel={`Refunding a payment on invoice #${invoice.invoiceNumber || ''}. Up to ${formatCurrency(refundCap, currencyCode)} can be refunded.`}
                    refundableAmount={refundCap}
                    currencyCode={currencyCode}
                    hasCardPayment={!!refundTarget.payment?.stripePaymentIntentId}
                    onSubmit={handleRefundPayment}
                />
            )}

            <PaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                invoice={invoice}
                currentUserProfile={currentUserProfile}
                quickbooksConnected={companySettings?.quickbooksConnected}
                onlinePayAvailable={canOrgTakeOnlinePayments(companySettings)} // payhide-7e4c1b05
            />

            <ApplyCreditModal
                isOpen={isApplyCreditOpen}
                onClose={() => setIsApplyCreditOpen(false)}
                invoice={invoice}
                currentUserProfile={currentUserProfile}
                onSuccess={() => window.location.reload()}
            />

            {unapplyTarget && (
                <ConfirmationModal
                    isOpen={!!unapplyTarget}
                    onClose={() => { if (!unapplyBusy) setUnapplyTarget(null); }}
                    onConfirm={handleUnapplyCredit}
                    title="Remove applied credit"
                    message={`Remove the ${formatCurrency(Math.abs(Number(unapplyTarget.payment?.amount) || 0), currencyCode)} account credit applied to this invoice? That amount returns to the customer's account credit balance, and this invoice's balance increases by the same amount.`}
                    confirmText={unapplyBusy ? 'Removing…' : 'Remove credit'}
                />
            )}

            <div className="p-4 sm:p-6 md:p-10 bg-gray-50 min-h-screen">
                <div className="max-w-4xl mx-auto">
                    <div className="flex flex-wrap justify-between items-center gap-3 mb-6 no-print">
                        <button
                            onClick={onBack}
                            className="flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800"
                        >
                            <ChevronLeft size={16} className="mr-1" />
                            Back to All Invoices
                        </button>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                            
                            {companySettings?.quickbooksConnected && (
                                isQuickBooksOriginInvoice(invoice.id, invoice) ? (
                                    /* qbguard-9e07c3d1 - origin badge: this invoice was imported
                                       FROM the client's QuickBooks, so it already exists there.
                                       Distinct from the green "Synced to QB" (= we pushed it). */
                                    <span className="flex items-center gap-1 text-sm text-slate-600 bg-slate-100 px-3 py-2 rounded-md font-medium border border-slate-300">
                                        <Archive size={14} /> In QuickBooks
                                    </span>
                                ) : invoice.qbInvoiceId ? (
                                    <span className="flex items-center gap-1 text-sm text-green-700 bg-green-100 px-3 py-2 rounded-md font-medium border border-green-200">
                                        <CheckCircle size={14} /> Synced to QB
                                    </span>
                                ) : canPushToQuickBooks(currentUserProfile) ? (
                                    /* s0rolegate-4c8a1f63 — office roles only; server: exportInvoiceToQuickBooks gate */
                                    <button
                                        onClick={handleExportToQuickBooks}
                                        disabled={isExporting}
                                        className="flex items-center gap-2 px-3 py-2 bg-white text-green-700 border border-green-600 rounded-md hover:bg-green-50 text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        {isExporting ? <Loader2 size={14} className="animate-spin"/> : <ArrowUpRight size={14} />}
                                        {isExporting ? 'Syncing...' : 'Export to QB'}
                                    </button>
                                ) : null
                            )}

                            {/* staffpay-6a2d9e47 — office roles only; server: createQuickBooksPayment gate */}
                            {/* s0bwoff-f74fb923 — no Pay Now on a written-off invoice (bad debt asks for nothing) */}
                            {invoice.status !== 'Paid' && invoice.status !== 'Written Off' && invoice.writtenOff !== true && canRecordPayment(currentUserProfile) && (
                                <button onClick={() => setIsPaymentModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-bold">
                                    <CreditCard size={16} /> Pay Now
                                </button>
                            )}

                            {/* creditapply-4d8b1e63 -- Seam 4a: conditional "Apply Credit" button (hidden,
                                not disabled, unless the customer has an applicable open credit and the
                                invoice has a balance — see showApplyCredit) AND the isCreditRow branch in
                                the Payment History table below both belong to this marker. Opens
                                ApplyCreditModal. */}
                            {showApplyCredit && (
                                <button onClick={() => setIsApplyCreditOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-bold">
                                    <CreditCard size={16} /> Apply Credit
                                </button>
                            )}
                            
                            <button 
                                onClick={handleSendEmail} 
                                disabled={isSending}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
                            >
                                {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                {isSending ? 'Sending...' : 'Email to Customer'}
                            </button>

                            {/* invlock-3d7f2a91 -- only finance roles (MANAGE_FINANCIALS) reach an
                                editable invoice; a Field Technician keeps the read-only detail
                                (amounts stay visible) but loses the Edit button. Mirrors the
                                firestore.rules isConsultant() gate shipping last. */}
                            {/* impro-3c8f1a57 -- an imported invoice is history: no Edit. Payments
                                (Record Payment on the customer, Apply Credit here) and Notes &
                                Media below still work. */}
                            {can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS) && (isImportedInvoice(invoice) ? (
                                <span
                                    title="This invoice was imported from your previous system, so it can't be edited. You can still record payments and add notes."
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-md text-sm cursor-default"
                                >
                                    <Lock size={16} /> Imported — view only
                                </span>
                            ) : (
                                <button onClick={() => onEditInvoice(invoice)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                    <Edit size={16} /> Edit
                                </button>
                            ))}
                            <div className="relative" ref={menuRef}>
                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    className="flex items-center gap-2 px-2 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                >
                                    <MoreVertical size={20} />
                                </button>
                                {isMenuOpen && (
                                    <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                        <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                                            <button onClick={handleDownloadPdf} className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">
                                                <Download size={16} />
                                                Download PDF
                                            </button>
                                            <button onClick={handlePrint} className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">
                                                <Printer size={16} />
                                                Print
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Inline feedback banner — replaces all alert() popups */}
                    {actionFeedback && (
                        <div className={`mb-4 p-3 rounded-lg flex items-start gap-2 text-sm no-print ${actionFeedback.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                            {actionFeedback.type === 'success' ? <CheckCircle size={16} className="shrink-0 mt-0.5" /> : <AlertTriangle size={16} className="shrink-0 mt-0.5" />}
                            <span>{actionFeedback.message}</span>
                            <button onClick={() => setActionFeedback(null)} className="ml-auto text-gray-400 hover:text-gray-600 shrink-0">✕</button>
                        </div>
                    )}

                    {/* recheckmsg-7a4c2e96 -- "Check again" found QuickBooks still differs: amber, stays until dismissed. */}
                    {recheckNotice && (
                        <div className="mb-4 p-3 rounded-lg flex items-start gap-2 text-sm no-print bg-amber-50 border border-amber-300 text-amber-900" role="status">
                            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
                            <span>{recheckNotice}</span>
                            <button onClick={() => setRecheckNotice(null)} aria-label="Dismiss" className="ml-auto text-amber-600 hover:text-amber-800 shrink-0">✕</button>
                        </div>
                    )}

                    {/* Inline send confirmation — replaces window.confirm() */}
                    {confirmSend && (
                        <div className="mb-4 p-4 rounded-lg bg-blue-50 border border-blue-200 flex flex-col sm:flex-row items-start sm:items-center gap-3 no-print">
                            <Send size={16} className="text-blue-600 shrink-0 mt-0.5 sm:mt-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-blue-800">
                                    Send this invoice to <strong>{invoice.customerDetails?.email}</strong>?
                                </p>
                                {attachChoices.length > 0 && ( // custcopy-32471cbd 8d — unticked by default; nothing qualifies = nothing shows
                                    <ul className="mt-2 space-y-1">
                                        {attachChoices.map(c => (
                                            <li key={c.id}>
                                                <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer">
                                                    <input type="checkbox" checked={!!attachTicks[c.id]}
                                                        onChange={e => toggleAttach(c, e.target.checked)}
                                                        className="rounded border-blue-300 text-blue-600 focus:ring-blue-500" />
                                                    <span>Attach {c.name} (PDF)</span>
                                                    {c.status !== 'complete' && <span className="text-xs text-amber-700">(not finished)</span>}
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {attachChoices.length > 0 && !canSendToAnyAddress(currentUserProfile) && ( // clfix-56ea256b M2 — the server refuses otherwise
                                    <p className="mt-1 text-xs text-blue-700">Checklists can be attached only when this email is on file for the customer or one of their properties.</p>
                                )}
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button onClick={() => setConfirmSend(false)} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">Cancel</button>
                                <button onClick={handleSendEmailConfirmed} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">Yes, Send</button>
                            </div>
                        </div>
                    )}

                    {/* qbdetailfail-5e1b9a34 -- read-only push-failure banner. RED, deliberately
                        distinct from the amber payment-sync banner below (different fact). No
                        retry button: the "Export to QB" button in the header IS the retry, and a
                        failed invoice already renders it. Not part of the printed invoice. */}
                    {showQbPushFailed && (
                        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-300 flex flex-col sm:flex-row items-start gap-3 no-print">
                            <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-red-900">This invoice could not be sent to QuickBooks.</p>
                                {invoice.qbPushError && (
                                    <p className="text-sm text-red-800 mt-1">QuickBooks said: "{invoice.qbPushError}"</p>
                                )}
                                {/* lasttried-4e8c2a91 -- qbPushErrorAt is a Firestore Timestamp;
                                    new Date(timestamp) is Invalid Date, so this read "Last tried on
                                    Invalid Date" on a direct load. Same coercion as the "Waiting
                                    since" banner: toJsDate first, and guard on it so a missing/
                                    malformed stamp omits the line rather than printing a bad date. */}
                                {toJsDate(invoice.qbPushErrorAt) && (
                                    <p className="text-xs text-red-700 mt-1">Last tried on {toJsDate(invoice.qbPushErrorAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}.</p>
                                )}
                                <p className="text-xs text-red-700 mt-2">Use the "Export to QB" button above to try again.</p>
                            </div>
                        </div>
                    )}

                    {/* qbdefer-2d6b8e50 -- Seam A "waiting to sync" banner. Amber, matching
                        the list badge and deliberately distinct from the red push-Failed banner
                        above: nothing broke, the automatic sync is blocked on a recoverable
                        condition (dead connection / accounts not configured) and will complete
                        once that's fixed or the invoice is pushed manually. Reason keys on the
                        exact backend values ('dead_connection' | 'accounts_unconfigured'), with
                        a plain fallback. no-print like the other action banners. */}
                    {showQbPushDeferred && (
                        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex flex-col sm:flex-row items-start gap-3 no-print">
                            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-amber-900">This invoice is waiting to sync to QuickBooks.</p>
                                {/* qba3copy-5b1d8a47 -- Seam A3: pre-QuickBooks auto-push block
                                    (reason 'push_precheck_failed'). At the moment of failure the
                                    push cannot tell whether the connection is dead or something
                                    else broke before QuickBooks was reached, so this copy avoids a
                                    specific-but-possibly-wrong "reconnect" instruction: it tells
                                    the operator it will be retried automatically, and to check the
                                    connection only IF it keeps waiting. Distinct from the
                                    accounts_unconfigured / dead_connection branches below; the
                                    generic fallback stays for any truly unknown reason. */}
                                {invoice.qbAutoSyncDeferredReason === 'push_precheck_failed' ? (
                                    <p className="text-sm text-amber-800 mt-1">Spatchy will keep trying to sync it automatically. If it's still waiting after a while, check your QuickBooks connection under Settings &gt; Integrations.</p>
                                ) : invoice.qbAutoSyncDeferredReason === 'tax_code_unconfigured' ? (
                                    <p className="text-sm text-amber-800 mt-1">No default tax code has been chosen for QuickBooks, so it couldn't sync automatically. Choose one under Settings &gt; Integrations, and it will sync on the next push.</p>
                                ) : invoice.qbAutoSyncDeferredReason === 'accounts_unconfigured' ? (
                                    <p className="text-sm text-amber-800 mt-1">Your QuickBooks accounting accounts aren't set up yet, so it couldn't sync automatically. Set them under Settings &gt; Integrations, and it will sync on the next push.</p>
                                ) : invoice.qbAutoSyncDeferredReason === 'dead_connection' ? (
                                    <p className="text-sm text-amber-800 mt-1">The QuickBooks connection needs attention, so it couldn't sync automatically. Reconnect QuickBooks under Settings &gt; Integrations, and it will sync on the next push.</p>
                                ) : (
                                    <p className="text-sm text-amber-800 mt-1">It couldn't sync automatically because QuickBooks needs attention. Check Settings &gt; Integrations.</p>
                                )}
                                {/* waitsince-6b2f4c81 -- qbAutoSyncDeferredAt is a Firestore
                                    Timestamp; new Date(timestamp) is Invalid Date, so the banner
                                    read "Waiting since Invalid Date". Coerce with toJsDate first
                                    (same primitive the helper now uses). A missing/malformed stamp
                                    → null → render the em-dash instead of a nonsensical date. */}
                                {toJsDate(invoice.qbAutoSyncDeferredAt) && (
                                    <p className="text-xs text-amber-700 mt-1">Waiting since {toJsDate(invoice.qbAutoSyncDeferredAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}.</p>
                                )}
                                <p className="text-xs text-amber-700 mt-2">Use the "Export to QB" button above to push it now.</p>
                            </div>
                        </div>
                    )}

                    {/* taxexempt3-8c4f2a1d -- the customer's tax exemption on this invoice (staff only), and the
                        office-only QuickBooks hold / tax-check marks. Renders nothing for everyone else. */}
                    <TaxExemptionBanner kind="invoice" document={invoice} orgId={currentUserProfile?.organizationId} currentUserProfile={currentUserProfile} />

                    {/* taxlines1-f2503245 -- a line's QuickBooks tax code couldn't be checked when this invoice was
                        made, so that line was charged the standard rate. Same amber look as the QuickBooks warning
                        below; no-print; staff page only. An office role's save in the editor clears it. */}
                    {lineTaxCheckOf(invoice) && (
                        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex flex-col sm:flex-row items-start gap-3 no-print" role="status" data-testid="line-tax-check-banner">
                            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-amber-900">{LINE_TAX_CHECK_TEXT}</p>
                            </div>
                        </div>
                    )}

                    {/* qbpushcheck-7e2c5a94 -- row 31 T1: QuickBooks holds a different total or tax than
                        the customer was billed. Amber like the other "needs attention" banners;
                        no-print. "Check again" (qbrecheck-3a7d9e52) is the one thing to click. */}
                    {qbTotalMismatchWords && (
                        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex flex-col sm:flex-row items-start gap-3 no-print">
                            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-amber-900">{qbTotalMismatchWords.headline}</p>
                                <p className="text-sm text-amber-800 mt-1">{qbTotalMismatchWords.spatchy}</p>
                                <p className="text-sm text-amber-800">{qbTotalMismatchWords.quickbooks}</p>
                                <p className="text-xs text-amber-700 mt-2">{qbTotalMismatchWords.advice}</p>
                                {/* qbrecheck-3a7d9e52 -- "Check again": Owner / Admin / Manager / Office Staff. */}
                                {canRecheckQuickBooksAmount(currentUserProfile) && (
                                    <div className="mt-3">
                                        <button type="button" onClick={handleRecheckQbTotal} disabled={isRechecking} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-60">
                                            {isRechecking && <Loader2 size={14} className="animate-spin" />}
                                            {isRechecking ? QB_RECHECK_WORDS.busy : QB_RECHECK_WORDS.button}
                                        </button>
                                        <p className="text-xs text-amber-700 mt-1">{QB_RECHECK_WORDS.hint}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* s0bunrec-1ef97499 — S0b: a card payment Stripe confirmed but that could NOT be added to
                        this invoice (recordStripePaymentOnce). Permanent: written only by the server, and no
                        button clears it (founder decision 2026-10-14) — the office resolves it by hand,
                        normally as a credit on the customer's account. */}
                    {Array.isArray(invoice.unrecordedPayments) && invoice.unrecordedPayments.length > 0 && (
                        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-300 flex items-start gap-3 no-print">
                            <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" />
                            <div className="flex-1 space-y-2">
                                <p className="text-sm font-semibold text-red-900">Payment received, not recorded</p>
                                {invoice.unrecordedPayments.map((u) => (
                                    <p key={u.paymentIntentId || u.at} className="text-sm text-red-800">
                                        A card payment of {formatCurrency(Number(u.amount) || 0, currencyCode)}{u.at ? ` on ${formatDate(new Date(u.at))}` : ''} was taken from the customer but was NOT added to this invoice, because {UNRECORDED_REASON_WORDS[u.reason] || 'the invoice could not take the payment'}.{u.paymentIntentId ? ` Stripe payment: ${u.paymentIntentId}.` : ''}
                                    </p>
                                ))}
                                <p className="text-sm text-red-800">
                                    The money is in your Stripe account. Record it by hand, normally as a credit on the customer's account: open the customer, go to Payment History, choose Record Payment, and untick "Apply to open invoices". Contact Spatchy support if you're unsure.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* s0bcharge-bc5e9925 — S0b: a card payment was REFUSED because this invoice's saved balance
                        is missing or doesn't match its payments (createPaymentIntent). No fix button by founder
                        decision (2026-10-14): Spatchy support corrects the balance. */}
                    {/* Shown only while the figures still disagree; the flag itself stays as support history. */}
                    {isBalanceReviewActive(invoice) && (
                        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex items-start gap-3 no-print">
                            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                {/* Plain words for office users (Marcus, 2026-10-14). One message for the missing and
                                    mismatched cases; the figures, the failed check and who tried to pay stay on the
                                    flag for Spatchy support and are deliberately not shown here. */}
                                <p className="text-sm font-semibold text-amber-900">Online card payment is paused for this invoice</p>
                                <p className="text-sm text-amber-800">
                                    Spatchy found that this invoice's internal records don't add up, so it has paused online card payments to avoid charging the customer the wrong amount. This usually happens with invoices imported from another system or created by an older version of Spatchy. The amount owing shown here is correct. You can still record cash, cheque or e-transfer payments as usual. Please contact Spatchy support to have it fixed.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* paysyncbanner-7d3e5b21 -- persisted payment-sync warning + acknowledge.
                        Amber, deliberately distinct from the red QB push-Failed badge. Not part
                        of the printed invoice, so no-print like the other action banners. */}
                    {showPaySyncWarning && (
                        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex flex-col sm:flex-row items-start gap-3 no-print">
                            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm text-amber-900">{paySyncInvoice.qbPaymentSyncError}</p>
                                {confirmDismiss && (
                                    <p className="text-xs text-amber-800 mt-2">
                                        Mark this QuickBooks payment reminder as handled? This only hides the reminder in Spatchy -- it does NOT send anything to QuickBooks. Only do this once you have recorded the payment in QuickBooks yourself (or decided you do not need to).
                                    </p>
                                )}
                            </div>
                            {canDismissPaySync && (
                                <div className="flex gap-2 shrink-0">
                                    {confirmDismiss ? (
                                        <>
                                            <button onClick={() => setConfirmDismiss(false)} disabled={isDismissing} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">Cancel</button>
                                            <button onClick={handleDismissPaySync} disabled={isDismissing} className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium">Yes, I have handled it</button>
                                        </>
                                    ) : (
                                        <button onClick={() => setConfirmDismiss(true)} className="px-3 py-1.5 text-sm bg-white border border-amber-400 rounded-md text-amber-800 hover:bg-amber-100 font-medium">Mark as handled</button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {paySyncHandled && (
                        <div className="mb-4 text-xs text-gray-500 flex items-center gap-2 no-print">
                            <span>Marked handled{paySyncInvoice.qbPaymentSyncAckBy?.name ? ` by ${paySyncInvoice.qbPaymentSyncAckBy.name}` : ''}{paySyncInvoice.qbPaymentSyncAckAt ? ` on ${formatDate(paySyncInvoice.qbPaymentSyncAckAt)}` : ''}.</span>
                            {canDismissPaySync && (
                                <button onClick={handleUndoPaySync} disabled={isDismissing} className="text-blue-600 hover:underline">Undo</button>
                            )}
                        </div>
                    )}

                    {/* invnotes-strip-2b8e4c63 -- working-record Notes & Media strip.
                        Standard collapsible strip (same as quotes/jobs), placed before
                        the printable invoice card and marked no-print (operator-only).
                        Attaches to the linked job when there is one, else the invoice
                        (mobile-parity rule, resolved above). The caption names the
                        destination before adding; allowFlagging={false} matches
                        QuoteDetailsView (no org-users list on this view). The existing
                        customer-facing preview + curation below is untouched. */}
                    {notesAttachedKey && (
                        <div className="mb-4 no-print">
                            <button
                                type="button"
                                onClick={() => setNotesExpanded(prev => !prev)}
                                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50"
                            >
                                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <MessageSquare size={16} /> Notes &amp; Media
                                    <span className="text-xs font-normal text-gray-400">({invoiceNotes.length})</span>
                                </span>
                                {notesExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                            </button>
                            {notesExpanded && (
                                <div className="mt-2 bg-white rounded-lg shadow-sm border border-gray-100 p-4 space-y-3">
                                    <p className="text-xs text-gray-500">
                                        {notesJobId
                                            ? "Notes and photos are saved to this job's record."
                                            : 'Notes and photos are saved to this invoice.'}
                                    </p>
                                    <NoteComposer
                                        attachedTo={notesAttachedTo}
                                        orgId={currentUserProfile?.organizationId}
                                        currentUserProfile={currentUserProfile}
                                        allowFlagging={false}
                                        placeholder={notesJobId ? 'Add a note or photo about this job…' : 'Add a note or photo about this invoice…'}
                                    />
                                    <div className="space-y-2 pt-1">
                                        {invoiceNotes.length === 0 ? (
                                            <p className="text-sm text-gray-500 text-center py-4">No notes yet — add the first one above.</p>
                                        ) : (
                                            invoiceNotes.map(note => (
                                                <NoteCard key={note.id} note={note} currentUserProfile={currentUserProfile} orgId={currentUserProfile?.organizationId} />
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div id="invoice-to-print" ref={invoiceRef} className="bg-white border border-gray-200 rounded-lg p-6 sm:p-10">
                        {/* Header Section */}
                        <div className="flex justify-between items-start mb-8 pb-8 border-b">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">{companySettings?.companyName || 'Your Company'}</h1>
                                {/* invhead-2d7c5b94 -- street, unit, then "City, Province Postal": the same lines as the invoice PDF (utils/letterhead). */}
                                {addressLines(companySettings).map((line, i) => (
                                    <p key={i} className="text-gray-500">{line}</p>
                                ))}
                                <p className="text-gray-500">{companySettings?.phone}</p>
                                <p className="text-gray-500">{companySettings?.email}</p>
                            </div>
                            <div className="text-right">
                                <h2 className="text-4xl font-bold text-gray-400 uppercase">Invoice</h2>
                            </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
                            <div>
                                <h3 className="text-xs text-gray-500 uppercase font-semibold mb-2">Customer</h3>{/* marker c3f6a1d8 -- Seam 2 relabel Billed To -> Customer */}
                                <div className="text-sm text-gray-800 not-italic">
                                    {/* --- CLICKABLE CUSTOMER NAME --- */}
                                    <button 
                                        onClick={() => onOpenCustomer({ id: invoice.customerId })}
                                        className="font-bold text-blue-600 hover:underline hover:text-blue-800 transition-colors text-left"
                                        title="View Customer Profile"
                                    >
                                        {customer?.name}
                                    </button>
                                    <p className="whitespace-pre-line">{formatAddress(customer?.address)}</p>
                                    <p>{customer?.email}</p>
                                </div>
                            </div>

                            {/* marker d9b4e7c2 -- Seam 2: BILL TO cell, only when a bill-to party is set.
                                Grid cell between CUSTOMER and Service Location, mirroring the PDF order. */}
                            {billTo && (
                                <div>
                                    <h3 className="text-xs text-gray-500 uppercase font-semibold mb-2">Bill To</h3>
                                    <div className="text-sm text-gray-800 not-italic">
                                        <p className="font-bold">{billTo.name || ''}</p>
                                        {billTo.company && <p>{billTo.company}</p>}
                                        {billToAddress && <p className="whitespace-pre-line">{billToAddress}</p>}
                                        {billTo.email && <p>{billTo.email}</p>}
                                    </div>
                                </div>
                            )}

                            {/* PROPERTIES v3 — Service location (only when the invoice is tied to a property) */}
                            {invoice.propertyId && (
                                <div>
                                    <h3 className="text-xs text-gray-500 uppercase font-semibold mb-2">Service Location</h3>
                                    <div className="text-sm text-gray-800 not-italic">
                                        <p className="font-bold">{invoice.propertyName || 'Property'}</p>
                                        <p className="whitespace-pre-line">{formatAddress(invoice.serviceAddress)}</p>
                                    </div>
                                </div>
                            )}

                            {/* jobnum-3e8c1f92 — min-w-0. This is a grid item, and a grid item's
                                default min-width:auto means it refuses to shrink below its widest
                                unbreakable content, so a long value here pushed sideways into the
                                Date Issued / Due Date cell instead of wrapping. min-w-0 lets it
                                wrap; the values inside carry break-words. */}
                            <div className="min-w-0">
                                <h3 className="text-xs text-gray-500 uppercase font-semibold">Invoice #</h3>
                                <p className="text-gray-800 font-medium break-words">{invoice.invoiceNumber || invoice.id.substring(0, 8)}</p>
                                {/* ROUND 3c VERIFIER: 1761744000-round3c-fix3-legacy-qb-number */}
                                {/* For imported invoices, show the original QB invoice number */}
                                {/* below the Spatchy one so users can cross-reference. */}
                                {invoice.source === 'import' && invoice.legacyInvoiceNumber && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        QB #{invoice.legacyInvoiceNumber}
                                    </p>
                                )}
                                
                                {showJobNumberBlock && ( /* jobnum-3e8c1f92 — was `invoice.linkedJobNumber &&` */
                                    <>
                                        <h3 className="text-xs text-gray-500 uppercase font-semibold mt-4">Job #</h3>
                                        {/* s2-jobwp-9d4f2a61 — SEAM 2. An invoice is its own document of
                                            record — its Work Performed is COPIED at creation and never
                                            changes afterwards — but it must stay possible to open the job
                                            it came from and check what was actually done. Record plus
                                            reference. Mirrors the Estimate # link below (invqlink-b7d2e9c4)
                                            exactly, fallback included: an invoice carrying a job number but
                                            no id renders as today's plain text, never a dead link. Resolve
                                            the id the way every other reader does (linkedJobId || jobId) —
                                            web and batchCreateInvoices write only the former, completeJob
                                            and mobile write both. A job since DELETED still links; the
                                            /jobs/:id deep-link handler says so (CrmApp s2-jobwp-9d4f2a61)
                                            rather than failing silently.

                                            jobsnew-5c9e3a17 — and it says so ONLY for this click. The
                                            `fromInvoice` navigation state below is what licenses that
                                            alert; without it the handler goes back to a silent
                                            console.warn. Seam 2 alerted on EVERY missing job, which
                                            fired on /jobs/new (Add Job) and told every user their new
                                            job had been deleted. The sentence is written for someone
                                            who came from an invoice, so only this button turns it on. */}
                                        {/* jobnum-3e8c1f92 — the value is now `displayJobNumber`, which is a
                                            job number or nothing; a doc id can no longer reach this line.
                                            With a link but no resolvable number the label degrades to
                                            "View job" rather than inventing one. `break-words` + the
                                            min-w-0 on the grid cell are the overflow guard: the cell is a
                                            grid item, whose default min-width:auto let a long unbroken
                                            value push into the Date Issued / Due Date column instead of
                                            wrapping. Belt and braces, since nothing long should render now. */}
                                        {linkedJobIdForNumber ? (
                                            <button
                                                onClick={() => navigate(`/jobs/${linkedJobIdForNumber}`, { state: { fromInvoice: true } })}
                                                className="font-medium text-blue-600 hover:underline hover:text-blue-800 transition-colors text-left break-words max-w-full"
                                                title="View Job"
                                            >
                                                {displayJobNumber || 'View job'}
                                            </button>
                                        ) : (
                                            <p className="text-gray-800 font-medium break-words">{displayJobNumber}</p>
                                        )}
                                    </>
                                )}

                                {/* linkedquote-8b3e5f21 -- Seam 2d/4b: show the source estimate number,
                                    stamped at creation (CrmApp linkedquote-4c7a9e63). Only present on
                                    invoices created from a quote AFTER that change; absent otherwise, so
                                    an invoice with no linked quote renders exactly as today. */}
                                {invoice.linkedQuoteNumber && (
                                    <>
                                        <h3 className="text-xs text-gray-500 uppercase font-semibold mt-4">Estimate #</h3>
                                        {/* invqlink-b7d2e9c4 -- link to the source estimate when its id is present (matches the
                                            customer-name link style above); falls back to plain text so a pre-link invoice with a
                                            number but no linkedQuoteId can never render a broken link. */}
                                        {/* jobnum-3e8c1f92 — break-words for symmetry with Job # above;
                                            same cell, same overflow risk if a number is ever long. */}
                                        {invoice.linkedQuoteId ? (
                                            <button
                                                onClick={() => navigate(`/quotes/${invoice.linkedQuoteId}`)}
                                                className="font-medium text-blue-600 hover:underline hover:text-blue-800 transition-colors text-left break-words max-w-full"
                                                title="View Estimate"
                                            >
                                                {invoice.linkedQuoteNumber}
                                            </button>
                                        ) : (
                                            <p className="text-gray-800 font-medium break-words">{invoice.linkedQuoteNumber}</p>
                                        )}
                                    </>
                                )}
                            </div>
                            
                            <div>
                                <h3 className="text-xs text-gray-500 uppercase font-semibold">Date Issued</h3>
                                <p className="text-gray-800 font-medium">{formatDate(invoice.createdAt)}</p>

                                <h3 className="text-xs text-gray-500 uppercase font-semibold mt-4">Due Date</h3>
                                <p className="text-gray-800 font-medium">{formatDate(invoice.dueDate)}</p>

                                {/* recinv-billperiod-7c1f9a04 -- Billing Period, ONLY when the
                                    recurring generator stamped both bounds (Timestamps written at
                                    index.js:1597-1598). Every ordinary invoice lacks these fields
                                    and renders unchanged. Coerce with toJsDate FIRST: these arrive
                                    as RAW Firestore Timestamps (the list mapper converts only
                                    createdAt/dueDate; the deep-link path passes raw data()), and
                                    formatDate returns 'N/A' on a raw Timestamp. The year is
                                    collapsed onto the end when both bounds share it. */}
                                {(() => {
                                    const s = toJsDate(invoice.billingPeriodStart);
                                    const e = toJsDate(invoice.billingPeriodEnd);
                                    if (!s || !e) return null;
                                    const sameYear = s.getFullYear() === e.getFullYear();
                                    const startStr = s.toLocaleDateString('en-US', sameYear
                                        ? { month: 'short', day: 'numeric' }
                                        : { month: 'short', day: 'numeric', year: 'numeric' });
                                    const endStr = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                    return (
                                        <>
                                            <h3 className="text-xs text-gray-500 uppercase font-semibold mt-4">Billing Period</h3>
                                            <p className="text-gray-800 font-medium">{startStr} – {endStr}</p>
                                        </>
                                    );
                                })()}
                            </div>
                            
                            <div className="sm:text-right">
                                <h3 className="text-xs text-gray-500 uppercase font-semibold">Status</h3>
                                <InvoiceStatusBadge status={invoice.status} />
                            </div>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">{invoice.title}</h3>
                        
                        {/* Table */}
                        <table className="w-full text-left mb-8">
                            <thead className="border-b">
                                <tr className="text-xs text-gray-600">
                                    <th className="p-2 font-semibold">Item / Description</th>
                                    <th className="hidden sm:table-cell p-2 text-right font-semibold whitespace-nowrap">Qty</th>
                                    <th className="hidden sm:table-cell p-2 text-right font-semibold whitespace-nowrap">Price</th>
                                    <th className="p-2 text-right font-semibold whitespace-nowrap">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.lineItems?.map((item, index) => {
                                    // ROUND 3c VERIFIER: 1761744000-round3c-fixA-item-shape
                                    // Normalize item shape so live (name/price) and imported
                                    // (description/unitPrice) line items both render correctly.
                                    const itemTitle = item.name || item.description || '';
                                    const itemSubDesc = item.name ? item.description : '';
                                    const itemPrice = item.price ?? item.unitPrice ?? 0;
                                    const itemTotal = item.total ?? ((item.quantity || 0) * itemPrice);
                                    return (
                                        <tr key={index} className="border-b">
                                            <td className="p-2 align-top break-words">
                                                <p className={LINE_NAME}>{item.name ? itemTitle : <LineDescription text={itemTitle} />}</p>
                                                {itemSubDesc && <span className={LINE_DESC}><LineDescription text={itemSubDesc} /></span>}
                                                {/* a phone hides the Qty and Price columns: they show here instead */}
                                                <span className="sm:hidden block mt-1 text-xs text-gray-500">{item.quantity} × {formatCurrency(itemPrice, currencyCode)}</span>
                                                {item.taxExempt && (
                                                    <p className="text-xs text-gray-500 italic mt-1">
                                                        (Tax Exempt #: {item.taxExemptionNumber || 'N/A'})
                                                    </p>
                                                )}
                                            </td>
                                            <td className={`hidden sm:table-cell p-2 text-right ${LINE_NUM}`}>{item.quantity}</td>
                                            <td className={`hidden sm:table-cell p-2 text-right ${LINE_NUM}`}>{formatCurrency(itemPrice, currencyCode)}</td>
                                            <td className={`p-2 text-right font-medium ${LINE_NUM}`}>{formatCurrency(itemTotal, currencyCode)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* s1-wpfirst-4e9c1b73 — SEAM 1. WAS: the raw invoice.workPerformed
                            field under its own "Work Performed" heading, which the server PDF
                            never printed — so this printed page and the customer's PDF showed
                            different content under different headings for the same invoice.
                            NOW it prints the SAME resolved items the PDF / email / portal print
                            (getOperatorInvoiceNotes -> buildCustomerNotesData, identical args),
                            under the customer's own heading, photos included. If the fetch
                            fails opNotes is null and this prints nothing — which is exactly how
                            the PDF degrades on a notes error, so the two still agree. */}
                        {opNotes?.items?.length > 0 && (
                            <div className="mt-6 mb-6 border-t pt-6">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Notes from your service provider</h3>
                                {opNotes.items.map((note, idx) => {
                                    const dateLabel = note.addedAtEpoch ? new Date(note.addedAtEpoch).toLocaleDateString() : '';
                                    const byline = note.authorName ? `${note.authorName}${dateLabel ? ` · ${dateLabel}` : ''}` : dateLabel;
                                    const photos = Array.isArray(note.media) ? note.media.filter((m) => m && m.type === 'photo' && m.url) : [];
                                    return (
                                        <div key={idx} className="mb-4 last:mb-0">
                                            {note.text && <p className="text-sm text-gray-700 whitespace-pre-line">{note.text}</p>}
                                            {photos.length > 0 && (
                                                <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                                                    {photos.map((m, midx) => (
                                                        <img key={midx} src={m.thumbUrl || m.url} alt="Service note attachment" className="w-full h-20 object-cover rounded border border-gray-200"/>
                                                    ))}
                                                </div>
                                            )}
                                            {byline && <p className="text-xs text-gray-400 mt-1">{byline}</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Footer Totals */}
                        <div className="flex justify-end">
                            <div className="w-full max-w-sm space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Subtotal:</span>
                                    <span className="font-medium">{formatCurrency(invoice.subtotal, currencyCode)}</span>
                                </div>
                                
                                {Object.entries(taxBreakdown).map(([name, amount], index) => (
                                    <div key={index} className="flex justify-between">
                                        <span className="text-gray-600">{name}:</span>
                                        <span className="font-medium">{formatCurrency(amount, currencyCode)}</span>
                                    </div>
                                ))}
                                
                                <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                                    <span className="text-gray-800">Grand Total ({currencyCode}):</span>
                                    <span>{formatCurrency(invoice.total, currencyCode)}</span>
                                </div>

                                {/* paymentrecon-2d4f8a1c -- Seam 2d/4a: reconcile the Footer Totals
                                    summary. Computation mirrors the invoice PDF EXACTLY
                                    (functions/index.js generateInvoiceHtml, marker paymentrecon-7e3a9c21)
                                    so the operator view and the customer document never state
                                    different numbers: read stored amountPaid/balanceDue first, fall
                                    back to summing paymentHistory, clamp a sub-cent balance to zero.
                                    An unpaid invoice (amountPaid === 0) renders exactly as today.
                                    QuickBooks partial imports (source 'import' && qbStatus 'partial')
                                    suppress the Balance Due row because their stored balance overstates
                                    what's owed. */}
                                {(() => {
                                    const paymentHistory = Array.isArray(invoice.paymentHistory) ? invoice.paymentHistory : [];
                                    // sdrefund3-sum-4e8b1a3c -- Seam D zero-edge fix. Read stored
                                    // amountPaid with ?? (not ||) so a full refund's stored 0 is
                                    // respected instead of being re-summed away. Keep the summary
                                    // visible whenever there is payment activity OR a positive stored
                                    // paid amount, so a fully-refunded invoice still shows Amount Paid
                                    // / Balance rather than hiding the block. Imported/settled invoices
                                    // (empty history, positive stored amountPaid) render exactly as
                                    // before; a genuinely unpaid invoice (no history, 0 paid) still
                                    // returns null. (The plan's bare !paymentHistory.length guard would
                                    // have hidden imported/settled summaries -- see report.)
                                    const amountPaid = invoice.amountPaid ??
                                        paymentHistory.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
                                    let balanceDue = invoice.balanceDue !== undefined ? invoice.balanceDue : (invoice.total - amountPaid);
                                    if (balanceDue < 0.01) balanceDue = 0;
                                    const suppressBalanceDue = invoice.source === 'import' && invoice.qbStatus === 'partial';
                                    if (!paymentHistory.length && !(amountPaid > 0)) return null;
                                    // refundscreen-7c3e5a18 -- an invoice with credit notes reads like its PDF,
                                    // emails and portal: one line per credit note (with a minus), "Net paid"
                                    // (no minus), then the balance. Shared rule: utils/invoiceCreditSummary.
                                    if (creditView) return (
                                        <>
                                            {creditView.lines.map((line, i) => (
                                                line.kind === 'credit' ? (
                                                    <div key={i} className="flex justify-between">
                                                        <span className="text-gray-600">{line.label}:</span>
                                                        <span className="font-medium text-orange-700">−{formatCurrency(line.amount, currencyCode)}</span>
                                                    </div>
                                                ) : line.kind === 'netPaid' ? (
                                                    <div key={i} className="flex justify-between">
                                                        <span className="text-gray-600">{line.label}:</span>
                                                        <span className="font-medium text-green-700">{formatCurrency(line.amount, currencyCode)}</span>
                                                    </div>
                                                ) : line.paidInFull ? (
                                                    <div key={i} className="flex justify-between font-bold border-t pt-2 mt-2">
                                                        <span className="text-green-700">{line.label}:</span>
                                                        <span className="text-green-700">Paid in full</span>
                                                    </div>
                                                ) : (
                                                    <div key={i} className="flex justify-between font-bold border-t pt-2 mt-2">
                                                        <span className="text-gray-800">{line.label}:</span>
                                                        <span>{formatCurrency(line.amount, currencyCode)}</span>
                                                    </div>
                                                )
                                            ))}
                                        </>
                                    );
                                    return (
                                        <>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Amount Paid:</span>
                                                <span className="font-medium text-green-700">-{formatCurrency(amountPaid, currencyCode)}</span>
                                            </div>
                                            {!suppressBalanceDue && (
                                                balanceDue <= 0.01 ? (
                                                    <div className="flex justify-between font-bold border-t pt-2 mt-2">
                                                        <span className="text-green-700">Balance Due:</span>
                                                        <span className="text-green-700">Paid in full</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-between font-bold border-t pt-2 mt-2">
                                                        <span className="text-gray-800">Balance Due:</span>
                                                        <span>{formatCurrency(balanceDue, currencyCode)}</span>
                                                    </div>
                                                )
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Tax ID footer */}
                        {companySettings?.taxId && (
                            <div className="mt-8 pt-4 border-t border-gray-200 text-sm text-gray-500">
                                Tax ID / GST / HST: {companySettings.taxId}
                            </div>
                        )}
                    </div>

                    {/* r1-3m-opnotes-section-2c7f9a4e -- Customer-facing notes on THIS
                        invoice. OUTSIDE #invoice-to-print, but s1-wpfirst-4e9c1b73 now renders
                        the SAME items inside the print region too, so the printed page and the
                        customer's PDF cannot disagree. Part 1 is the faithful, read-only render
                        of exactly what the customer sees (items, == the PDF); Work Performed,
                        when present, is the first item and labels itself in its own byline.
                        Part 2 is the curation control: notes pulled
                        in from the linked job, each with a plainly-labeled Remove that edits
                        this invoice's included list only -- NOT a delete (that's NoteCard's
                        trash). */}
                    {(opNotesLoading || (opNotes && ((opNotes.items && opNotes.items.length > 0) || (opNotes.jobNotes && opNotes.jobNotes.length > 0)))) && (
                        <div className="mt-8 border-t pt-6 no-print">
                            <h3 className="text-lg font-bold text-gray-800 mb-1">Customer-facing notes</h3>
                            {/* s1-wpfirst-4e9c1b73 — the printed page renders these too now. */}
                            <p className="text-sm text-gray-500 mb-4">These appear on the customer's copy of this invoice — the PDF, the emailed invoice, the portal, and the printed page.</p>

                            {opNotesLoading && (
                                <p className="text-sm text-gray-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading notes…</p>
                            )}

                            {!opNotesLoading && opNotes?.items?.length > 0 && (
                                <div className="mb-5">
                                    {/* s1-wpfirst-4e9c1b73 — DELETED the "Showing the Work Performed
                                        text — this invoice has no included notes." caption. It existed
                                        to explain why one entry had no author, and it asserted there
                                        were no notes — which Seam 1 makes false, since Work Performed
                                        now LEADS a list that may also carry notes. The entry labels
                                        itself in its own byline ("Work performed · date"), the same
                                        label the customer sees, so the caption has nothing left to say.
                                        Its authorName === null test would match nothing either. */}
                                    <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                                        {opNotes.items.map((note, idx) => {
                                            const dateLabel = note.addedAtEpoch ? new Date(note.addedAtEpoch).toLocaleDateString() : '';
                                            const byline = note.authorName ? `${note.authorName}${dateLabel ? ` · ${dateLabel}` : ''}` : dateLabel;
                                            const photos = Array.isArray(note.media) ? note.media.filter((m) => m && m.type === 'photo' && m.url) : [];
                                            return (
                                                <div key={idx} className="p-4">
                                                    {note.text && <p className="text-gray-700 whitespace-pre-line text-sm">{note.text}</p>}
                                                    {photos.length > 0 && (
                                                        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                                                            {photos.map((m, midx) => (
                                                                <a key={midx} href={m.url} target="_blank" rel="noopener noreferrer" className="block rounded-md overflow-hidden border border-gray-200 bg-white">
                                                                    <img src={m.thumbUrl || m.url} alt="Service note attachment" loading="lazy" className="w-full h-20 object-cover"/>
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {byline && <p className="text-xs text-gray-400 mt-2">{byline}</p>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {!opNotesLoading && opNotes?.jobNotes?.length > 0 && (() => {
                                const includedSet = opNotes.hasInclusionList ? new Set(opNotes.includedNoteIds || []) : null;
                                const rows = opNotes.jobNotes.filter((n) => !includedSet || includedSet.has(n.id));
                                if (rows.length === 0) return null;
                                return (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <p className="text-sm font-semibold text-amber-900 mb-1">Notes pulled in from the job</p>
                                        <p className="text-xs text-amber-700 mb-3">Remove hides a note from this invoice only. It does <span className="font-semibold">not</span> delete the note from the job.</p>
                                        <div className="space-y-2">
                                            {rows.map((n) => (
                                                <div key={n.id} className="flex items-start justify-between gap-3 bg-white border border-amber-200 rounded-md px-3 py-2">
                                                    <span className="text-sm text-gray-700 flex-1 min-w-0">
                                                        <span className="block truncate">{n.preview}</span>
                                                        {n.photoCount > 0 && <span className="text-xs text-gray-400">{n.photoCount} photo{n.photoCount !== 1 ? 's' : ''}</span>}
                                                    </span>
                                                    <button
                                                        onClick={() => handleRemoveInvoiceNote(n.id)}
                                                        disabled={removingNoteId === n.id}
                                                        className="shrink-0 text-xs font-medium text-amber-800 border border-amber-300 rounded px-2 py-1 hover:bg-amber-100 disabled:opacity-50"
                                                    >
                                                        {removingNoteId === n.id ? 'Removing…' : 'Remove from invoice'}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* sdrefund3-rem-3b6e8a29 -- a refund reopens the balance but mutes the
                        scheduled dunning (Marcus decision 1). When the reopened balance is
                        genuinely owed, an operator turns chasing back on here. Shown only
                        while remindersMuted is set. */}
                    {invoice.remindersMuted && (
                        <div className="mt-8 no-print rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2">
                                <BellOff size={18} className="text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold text-amber-900">Payment reminders are paused</p>
                                    <p className="text-sm text-amber-800">A refund reopened this invoice's balance, so automatic reminders were paused. Resume them if this balance is genuinely owed.</p>
                                </div>
                            </div>
                            <button
                                onClick={handleResumeReminders}
                                disabled={reminderBusy}
                                className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-amber-900 border border-amber-300 rounded px-3 py-1.5 hover:bg-amber-100 disabled:opacity-50"
                            >
                                {reminderBusy ? <Loader2 size={14} className="animate-spin"/> : <Bell size={14}/>} Resume reminders
                            </button>
                        </div>
                    )}

                    {/* sdrefund5-5c1e9b28 -- Seam D Piece 5: a card refund that Stripe left
                        PENDING (the connected balance was short) or that FAILED later.
                        These render from the invoice-level fields the webhook writes
                        (refundPending* / refundFailed*): the ledger only moves on
                        settlement, so a pending refund is not yet in Payment History, and
                        a failed one never moved at all. Both are muted on print. */}
                    {invoice.refundPending && (
                        <div className="mt-8 no-print rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-2">
                            <Clock size={18} className="text-amber-600 mt-0.5 shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-amber-900">Refund pending</p>
                                <p className="text-sm text-amber-800">
                                    A card refund{typeof invoice.refundPendingAmount === 'number' ? ` of ${formatCurrency(invoice.refundPendingAmount, currencyCode)}` : ''} is still being processed by the bank. It will appear here as a Refund once it settles — usually within a few business days. No action needed.
                                </p>
                            </div>
                        </div>
                    )}
                    {invoice.refundFailed && (
                        <div className="mt-8 no-print rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-2">
                            <XCircle size={18} className="text-red-600 mt-0.5 shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-red-900">Refund failed — the money was not returned</p>
                                <p className="text-sm text-red-800">
                                    The last card refund could not be completed{invoice.refundFailedReason ? ` (${invoice.refundFailedReason})` : ''}. Nothing was taken back from the customer. Try the refund again, or issue it in Stripe directly.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* --- PAYMENT HISTORY SECTION --- */}
                    {/* ROUND 3c VERIFIER: 1761744000-round3c-fixC-synthetic-payment-row */}
                    {/* Imported paid invoices have empty paymentHistory by design (Round 3a put */}
                    {/* payments in customers/{cid}/importedPayments instead). For those, render a */}
                    {/* single synthetic row showing the invoice was paid via import — no method */}
                    {/* or reference is fabricated. */}
                    {(() => {
                        const isImportedPaid = invoice.source === 'import' && invoice.status === 'Paid';
                        const isStatusSettled = invoice.settledViaStatus === true;
                        const hasLivePayments = invoice.paymentHistory && invoice.paymentHistory.length > 0;
                        // impayrows-3d84c6f1 -- real imported-payment docs (invoiceId back-link)
                        // also open the section, so imported Partial/Unpaid invoices with real
                        // payment docs show them. Sorted date-ascending with the SAME
                        // Timestamp handling as CustomerFormModal's mergedPaymentLedger.
                        const hasImportedPaymentDocs = importedInvoicePayments.length > 0;
                        const importedRows = [...importedInvoicePayments].sort((a, b) => {
                            const aMs = a.date?.toDate ? a.date.toDate().getTime() : (a.date ? new Date(a.date).getTime() : 0);
                            const bMs = b.date?.toDate ? b.date.toDate().getTime() : (b.date ? new Date(b.date).getTime() : 0);
                            return aMs - bMs;
                        });
                        if (!isImportedPaid && !isStatusSettled && !hasLivePayments && !hasImportedPaymentDocs) return null;
                        return (
                            <div className="mt-8 border-t pt-6 no-print">
                                <h3 className="text-lg font-bold text-gray-800 mb-4">Payment History</h3>
                                <div className="bg-white border rounded-lg overflow-hidden">
                                    <table className="w-full text-sm text-left text-gray-500">
                                        <thead className="bg-gray-50 text-xs text-gray-700 uppercase">
                                            <tr>
                                                <th className="px-4 py-3">Date</th>
                                                <th className="px-4 py-3">Method</th>
                                                <th className="px-4 py-3">Reference</th>
                                                <th className="px-4 py-3">Amount</th>
                                                <th className="px-4 py-3 text-right">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {/* impayrows-3d84c6f1 -- one real row per importedPayments doc */}
                                            {hasImportedPaymentDocs && importedRows.map((p) => {
                                                const d = p.date?.toDate ? p.date.toDate() : (p.date ? new Date(p.date) : null);
                                                return (
                                                    <tr key={p.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">{d ? formatDate(d) : '—'}</td>
                                                        <td className="px-4 py-3 font-medium text-gray-900">{p.method || '—'}</td>
                                                        <td className="px-4 py-3">{p.reference || '—'}</td>
                                                        <td className="px-4 py-3 font-bold text-green-600">{formatCurrency(p.amount, currencyCode)}</td>
                                                        <td className="px-4 py-3 text-right">
                                                            <span className="text-gray-500 flex items-center justify-end gap-1">
                                                                <CheckCircle size={14}/> Imported
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {/* impayrows-3d84c6f1 -- synthetic row only when NO real payment docs
                                                matched (the GC Septic case: its payments carry no invoiceId).
                                                Markup below is unchanged. */}
                                            {isImportedPaid && !hasLivePayments && !hasImportedPaymentDocs && (
                                                <tr className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">{getInvoicePaidDate(invoice) ? formatDate(getInvoicePaidDate(invoice)) : '—'}</td>{/* r3-paiddate — imported: no owned paid date → em-dash, not createdAt */}
                                                    <td className="px-4 py-3 font-medium text-gray-900">Imported invoice payment</td>
                                                    <td className="px-4 py-3">—</td>
                                                    <td className="px-4 py-3 font-bold text-green-600">{formatCurrency(invoice.total, currencyCode)}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <span className="text-gray-500 flex items-center justify-end gap-1">
                                                            <CheckCircle size={14}/> Imported
                                                        </span>
                                                    </td>
                                                </tr>
                                            )}
                                            {/* SEAM 1 — invoice marked Paid via the status dropdown (no online payment). */}
                                            {isStatusSettled && !hasLivePayments && (
                                                <tr className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">{getInvoicePaidDate(invoice) ? formatDate(getInvoicePaidDate(invoice)) : '—'}</td>{/* r3-paiddate */}
                                                    <td className="px-4 py-3 font-medium text-gray-900">Marked as paid</td>
                                                    <td className="px-4 py-3">—</td>
                                                    <td className="px-4 py-3 font-bold text-green-600">{formatCurrency(invoice.amountPaid ?? invoice.total, currencyCode)}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <span className="text-gray-500 flex items-center justify-end gap-1">
                                                            <CheckCircle size={14}/> Marked paid
                                                        </span>
                                                    </td>
                                                </tr>
                                            )}
                                            {hasLivePayments && invoice.paymentHistory.map((payment, idx) => {
                                                // sdrefund3-invrow-7c2d9f41 -- a refund entry (isRefund flag,
                                                // or a negative amount) reads as a Refund everywhere: orange
                                                // "Refund" badge, warm amount shown as a POSITIVE figure
                                                // (Math.abs), never the green payment treatment. A positive
                                                // payment renders exactly as before. The Refund action is
                                                // offered only on a real (non-refund) payment when the
                                                // invoice still shows money paid to hand back.
                                                const isRefundRow = payment.isRefund || Number(payment.amount) < 0;
                                                // Seam 4a -- an applied account credit (tagged isCredit by
                                                // applyCreditToInvoice) reads as an "Applied credit": purple
                                                // badge, positive green amount (it reduced the balance like a
                                                // payment), a neutral status (no QB sync in v1), and an Unapply
                                                // action (Seam 4b) in place of Refund.
                                                const isCreditRow = payment.isCredit === true;
                                                // creditunapply-2f6b8d41 -- Seam 4b: the negative row a credit
                                                // unapply appends (tagged isCreditReversal). It reads as a neutral
                                                // "Credit removed" row -- NOT the orange refund treatment (its
                                                // amount<0 would otherwise trip isRefundRow); no action on it.
                                                const isCreditReversalRow = payment.isCreditReversal === true;
                                                // A live (still-applied) credit row offers Unapply; one whose
                                                // application has already been reversed does not.
                                                const creditRowReversed = isCreditRow && reversedCreditEntryIdsHere.has(payment.creditEntryId);
                                                // refundscreen-7c3e5a18 -- R1d-1. rowView: on an invoice with credit notes the
                                                // internal +credit-note line is hidden and a refund is named. rowAction: Refund
                                                // for Owner / Admin / Manager only; a company without the new flow keeps its old
                                                // line rule; with it, real payment lines only and a hint on a deposit line.
                                                const rowView = paymentRowView(payment, creditView);
                                                if (rowView.hidden) return null;
                                                const rowAction = refundRowAction(payment, { flagOn: refundFlowOn, user: currentUserProfile, invoiceNetPaid });
                                                return (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">{payment.date ? formatDate(normalizePaidDate(payment.date)) : '—'}</td>{/* r3-paiddate */}
                                                    <td className="px-4 py-3 font-medium text-gray-900">
                                                        {isCreditReversalRow ? 'Credit removed' : isRefundRow ? (rowView.refundLabel || 'Refund') : payment.method}
                                                        {isRefundRow && !isCreditReversalRow && !rowView.refundLabel && (
                                                            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Refund</span>
                                                        )}
                                                        {isCreditReversalRow && (
                                                            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">Credit removed</span>
                                                        )}
                                                        {isCreditRow && (
                                                            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Applied credit</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {/* refundstatus-8a3d6f12 -- a new-flow refund's reference is its credit note (not a repeat of Method), with the note's email status, Resend and Download. */}
                                                        {(rowView.reference !== null ? rowView.reference : payment.reference) || '—'}
                                                        {rowView.creditNoteId && (
                                                            <CreditNoteLine
                                                                creditNoteId={rowView.creditNoteId}
                                                                creditNoteNumber={rowView.creditNoteNumber}
                                                                creditNote={creditNoteDocs[rowView.creditNoteId]}
                                                                canSend={refundFlowOn && canRefundInvoice(currentUserProfile)}
                                                                orgId={currentUserProfile?.organizationId}
                                                            />
                                                        )}
                                                    </td>
                                                    <td className={`px-4 py-3 font-bold ${isCreditReversalRow ? 'text-gray-600' : isRefundRow ? 'text-orange-700' : 'text-green-600'}`}>
                                                        {formatCurrency(Math.abs(Number(payment.amount) || 0), currencyCode)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <div className="flex flex-col items-end gap-1">
                                                            {rowView.refundId ? (
                                                                /* refundstatus-8a3d6f12 -- a new-flow refund shows its OWN QuickBooks status (from its
                                                                   record) with Retry; someone who can't read refund records sees no status. */
                                                                refundStatusOn
                                                                    ? <RefundQbStatus rec={refundRecords[rowView.refundId]} invoiceId={invoice.id} refundId={rowView.refundId} companySettings={companySettings} />
                                                                    : <span className="text-gray-400">—</span>
                                                            ) : isCreditReversalRow ? (
                                                                <span className="text-gray-500 flex items-center justify-end gap-1">
                                                                    <RotateCcw size={14}/> Removed
                                                                </span>
                                                            ) : isCreditRow ? (
                                                                <span className="text-gray-500 flex items-center justify-end gap-1">
                                                                    <CheckCircle size={14}/> Applied
                                                                </span>
                                                            ) : payment.syncError ? (
                                                                <span className="text-red-600 flex items-center justify-end gap-1" title={payment.syncError}>
                                                                    <AlertTriangle size={14}/> QB Failed
                                                                </span>
                                                            ) : payment.qbPaymentId && payment.qbPaymentId !== 'skipped' ? (
                                                                <span className="text-green-600 flex items-center justify-end gap-1">
                                                                    <CheckCircle size={14}/> Synced
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-400">Local Only</span>
                                                            )}
                                                            {rowAction === 'refund' && (
                                                                <button
                                                                    onClick={() => (refundFlowOn ? setRefundDialogTarget({ payment, idx }) : setRefundTarget({ payment, idx }))}
                                                                    className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 hover:text-orange-800"
                                                                >
                                                                    <RotateCcw size={12}/> Refund
                                                                </button>
                                                            )}
                                                            {rowAction === 'depositHint' && (
                                                                <span className="text-xs text-gray-500">{DEPOSIT_HINT}</span>
                                                            )}
                                                            {/* creditunapply-2f6b8d41 -- Seam 4b: Unapply a still-live applied credit (the slot 4a left open by suppressing Refund on credit rows). */}
                                                            {isCreditRow && !creditRowReversed && can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS) && (
                                                                <button
                                                                    onClick={() => setUnapplyTarget({ payment, idx })}
                                                                    className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-800"
                                                                >
                                                                    <RotateCcw size={12}/> Unapply
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })()}
                    {/* ------------------------------------- */}
                </div>
            </div>
        </>
    );
};

// taxexempt3-8c4f2a1d — after "Charge tax on this document" / "Apply exemption again" the page shows the
// document as the server now has it (TaxExemptionBanner re-reads it), without closing the page.
export default withExemptionRefresh(InvoiceDetailsView, 'invoice');